import {
  formatNumberWithCommas,
  formatNumberWithCommasButNoDecimalPoints,
} from "./helpers";

/** Aligns with `BrandFeatureStatusMapping` / `FeatureChoices` / `SubFeatureChoices` on backend. */
export const CAMPAIGN_INTELLIGENCE_FEATURE_KEY = "campaign_intelligence";
export const CAMPAIGN_INTELLIGENCE_SUBFEATURE_KEY =
  "campaign_intelligence_access";
/** Min linked Wishlink posts on a tracking campaign before showing Intelligence CTA. */
export const CAMPAIGN_INTELLIGENCE_MIN_WISHLINK_POSTS = 10;

const safeFormatter = (value) => (value != null ? value.toFixed(2) : "-");
export const SourceFieldsName = [
  { id: "IG", value: "IG", label: "IG" },
  { id: "YT", value: "YT", label: "YT" },
];
export const TrendsComparisonPeriodViewName = [
  {
    id: "dod",
    label: "DAY-ON-DAY",
    value: "dod",
    backendKey: "day_on_day",
  },
  {
    id: "wow",
    label: "WEEK-ON-WEEK",
    value: "wow",
    backendKey: "week_on_week",
  },
  {
    id: "mom",
    label: "MONTH-ON-MONTH",
    value: "mom",
    backendKey: "month_on_month",
  },
];
export const ToDateFieldsName = [
  {
    id: "mtd",
    label: "Month-Till-Date",
    value: "MTD",
    backendKey: "MTD",
  },
  {
    id: "wtd",
    label: "Week-Till-Date",
    value: "WTD",
    backendKey: "WTD",
  },
  {
    id: "today",
    label: "Today",
    value: "Today",
    backendKey: "TODAY",
  },
];

export const SalesOverviewMetricsFields = [
  {
    backendKey: "Sale",
    label: "Sale",
    description: "Total sales recorded within specified daterange",
    formatter: (val) => `₹${formatNumberWithCommas(val)}`,
    show_red_dot: false,
  },
  {
    backendKey: "Orders",
    label: "Orders",
    description: "Total orders recorded within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Clicks",
    label: "Clicks",
    description: "Total Clicks on product links within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Unique Clicks",
    label: "Unique Clicks",
    description:
      "Unique Clicks (by user) on product links within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "AOV",
    label: "AOV",
    description: "Average order value of all orders within chosen daterange",
    formatter: (val) => `₹${formatNumberWithCommasButNoDecimalPoints(val)}`,
  },
  {
    backendKey: "Acquisition Conversion %",
    label: "Acquisition Conversion %",
    description:
      "% visitors who placed an order, out of all visitors to your website/app within chosen daterange",
    // formatter: (val) => `${formatNumberWithCommas(val)}%`,
    formatter: formatNumberWithCommas,
  },
];
export const ContentOverviewMetricsFields = [
  {
    backendKey: "Posts",
    label: "Posts",
    description: "Total posts created within specified daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Impressions",
    label: "Impressions",
    description: "Total impressions generated within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Links Shared",
    label: "Links Shared",
    description: "Total product links shared within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Creators",
    label: "Creators",
    description: "Total active creators within chosen daterange",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Sale Active Creators",
    label: "Sale Active Creators",
    description: "Creators with sales within chosen daterange",
    formatter: formatNumberWithCommas,
  },
];

export const NmvOverviewMetricsFields = [
  {
    backendKey: "GMV Pending to be Reconciled",
    label: "GMV Pending to be Reconciled",
    formatter: (val) => `₹${formatNumberWithCommasButNoDecimalPoints(val)}`,
    show_red_dot: true,
  },
  {
    backendKey: "Reconciled GMV",
    label: "Reconciled GMV",
    formatter: (val) => `₹${formatNumberWithCommasButNoDecimalPoints(val)}`,
  },
  {
    backendKey: "NMV% (of Reconciled GMV)",
    label: "NMV% (of Reconciled GMV)",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "Effective Commission%",
    label: "Effective Commission%",
    formatter: formatNumberWithCommas,
  },
];

export const ViewCampaignOverviewMetricsFields = [
  {
    backendKey: "views",
    label: "Views",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "clicks",
    label: "Clicks",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "orders",
    label: "Orders",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "gmv",
    label: "Sales",
    formatter: (val) => `₹${formatNumberWithCommasButNoDecimalPoints(val)}`,
  },
  {
    backendKey: "CPV",
    label: "CPV",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "CPC",
    label: "CPC",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "CPE",
    label: "CPE",
    formatter: formatNumberWithCommas,
  },
  {
    backendKey: "engagement_rate",
    label: "Engagement Rate (%)",
    formatter: formatNumberWithCommas,
  },
];

export const SalesTrendsFieldsName = [
  {
    id: "saleAmount",
    backendKey: "total_sale_amount",
    label: "Sale Amount(₹)",
    customHeader: "Total Sales",
    sortable: true,
    showTooltip: true,
    isVisible: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "orderCount",
    backendKey: "total_orders",
    label: "Orders",
    customHeader: "Total Orders",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "clicks",
    backendKey: "total_clicks",
    label: "Total Clicks",
    customHeader: "User Clicks",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "uniqueClicks",
    backendKey: "unique_clicks",
    label: "Unique Clicks",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "conversionRate",
    backendKey: "acquisition_conversion_percentage",
    label: "Acquisition Conversion %",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "%",
  },
  {
    id: "aov",
    backendKey: "aov",
    label: "AOV(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "postCount",
    backendKey: "total_posts",
    label: "Posts",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "impressionCount",
    backendKey: "total_impressions",
    label: "Impressions",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "sharedLinks",
    backendKey: "links_shared",
    label: "Links Shared",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "creatorCount",
    backendKey: "total_creators",
    label: "Total Creators",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "activeCreators",
    backendKey: "sale_active_creators",
    label: "Sale Active Creators",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
];
export const SalesRawDataFieldsName = [
  {
    id: "platform",
    backendKey: "platform",
    label: "Platform",
    customHeader: "Platform",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "amount",
    backendKey: "amount",
    label: "Amount(₹)",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "orderId",
    backendKey: "order_id",
    label: "Order ID",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "saleTime",
    backendKey: "sale_timestamp_ist",
    label: "Sale Timestamp",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "items",
    backendKey: "items",
    label: "Items",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "brandCommissionPct",
    backendKey: "brand_commission_pct",
    label: "BC%",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "clickTime",
    backendKey: "click_time",
    label: "Click Time",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "reconciled",
    backendKey: "reconciled",
    label: "Reconciled(T/F)",
    sortable: true,
    showTooltip: true,
  },
  // { id: 'nmv', backendKey: 'nmv', label: 'NMV(₹)', sortable: true, showTooltip: true },
];
export const ContentTrendsFieldsName = [
  {
    id: "postCount",
    backendKey: "total_posts",
    label: "Posts",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "impressionCount",
    backendKey: "total_impressions",
    label: "Impressions",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "sharedLinks",
    backendKey: "links_shared",
    label: "Links Shared",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "creatorCount",
    backendKey: "total_creators",
    label: "Total Creators",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "activeCreators",
    backendKey: "sale_active_creators",
    label: "Sale Active Creators",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "saleAmount",
    backendKey: "total_sale_amount",
    label: "Sale Amount(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "orderCount",
    backendKey: "total_orders",
    label: "Orders",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "clicks",
    backendKey: "total_clicks",
    label: "Total Clicks",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "uniqueClicks",
    backendKey: "unique_clicks",
    label: "Unique Clicks",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "conversionRate",
    backendKey: "conversion_percentage",
    label: "Conversion %",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "%",
  },
  {
    id: "aov",
    backendKey: "aov",
    label: "AOV(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
];
export const ContentRawDataFieldsName = [
  {
    id: "postedDate",
    backendKey: "posted_on",
    label: "Posted On",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "postTitle",
    backendKey: "title",
    label: "Title",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "postUrl",
    backendKey: "post_url",
    label: "Post URL",
    sortable: true,
    showTooltip: true,
    isClickable: true,
  },
  {
    id: "creatorChannel",
    backendKey: "username",
    label: "Creator/Channel",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "platform",
    backendKey: "platform",
    label: "Platform (IG/YT)",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "wishlinkPostUrl",
    backendKey: "wishlink_post_url",
    label: "Wishlink Post URL",
    sortable: true,
    showTooltip: true,
    isClickable: true,
  },
  {
    id: "impressionCount",
    backendKey: "impressions",
    label: "Impressions",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "likeCount",
    backendKey: "total_likes",
    label: "Likes",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "commentCount",
    backendKey: "total_comments",
    label: "Comments",
    sortable: true,
    showTooltip: true,
  },
];
export const NmvTrendsFieldsName = [
  {
    id: "gmv",
    backendKey: "gmv",
    label: "GMV(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "ordersCount",
    backendKey: "total_order",
    label: "Total Orders",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
  {
    id: "nmv",
    backendKey: "nmv",
    label: "NMV(₹)",
    sortable: true,
    showTooltip: true,
    unit: "Rs",
  },
  {
    id: "effectiveCommissionPercentage",
    backendKey: "effective_commission_percentage",
    label: "Effective Commission %",
    sortable: true,
    showTooltip: true,
    unit: "%",
  },
  {
    id: "pendingGMV",
    backendKey: "gmv_pending_reconciliation",
    label: "GMV Pending for Reconciliation(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "reconciledGMV",
    backendKey: "reconciled_gmv",
    label: "Reconciled GMV(₹)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "nmvPercentage",
    backendKey: "nmv_percentage_of_reconciled_gmv",
    label: "NMV % (of Reconciled GMV)",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "%",
  },
  {
    id: "effectiveCommission",
    backendKey: "effective_commission",
    label: "Effective Commission",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "Rs",
  },
  {
    id: "reconciledGMVPercentage",
    backendKey: "reconciled_gmv_percentage",
    label: "Reconciled GMV %",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
    unit: "%",
  },
  {
    id: "reconciledOrdersCount",
    backendKey: "reconciled_orders_count",
    label: "# of Reconciled Orders",
    sortable: true,
    showTooltip: true,
    formatter: safeFormatter,
  },
];
export const TrendsTableCoreColumns = [
  {
    id: "brandId",
    backendKey: "brand_id",
    label: "Brand IDs",
    sortable: true,
    showTooltip: true,
    render: (row) =>
      Array.isArray(row.brand_ids) ? row.brand_ids.join(", ") : row.brand_ids,
  },
  {
    id: "date",
    backendKey: "date",
    label: "Date",
    sortable: true,
    showTooltip: true,
  },
];
export const ReconciliationPendingOrderFieldsName = [
  {
    id: "brandId",
    backendKey: "brand_id",
    customHeader: "Brand Id",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "saleTimestamp",
    backendKey: "sale_timestamp_ist",
    customHeader: "Sale Timestamp",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "date",
    backendKey: "sale_date",
    customHeader: "Date",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "orderId",
    backendKey: "order_id",
    customHeader: "Order Id",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "orderAmount",
    backendKey: "amount",
    customHeader: "Order Amount(₹)",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "orderDetails",
    backendKey: "items",
    customHeader: "Order Details",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "newUser",
    backendKey: "new_user",
    customHeader: "New User",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "brandCommissionPct",
    backendKey: "brand_commission_pct",
    customHeader: "BC%",
    showTooltip: true,
    sortable: true,
    showHeaderTooltip: true,
    headerTooltipValue: "Brand Commission Percentage",
  },
  {
    id: "orderStatus",
    backendKey: "order_status",
    customHeader: "Order Status",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "refundAmount",
    backendKey: "refund_amount",
    customHeader: "Refund Amount(₹)",
    showTooltip: true,
    sortable: true,
  },
];
export const ReconciliationPreviousUploadsTableFieldsName = [
  {
    id: "uploadedOn",
    backendKey: "uploaded_on",
    customHeader: "Uploaded on",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "uploadedBy",
    backendKey: "uploaded_by",
    customHeader: "Uploaded by",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "dateRange",
    backendKey: "date_range",
    customHeader: "Date Range of orders",
    showTooltip: true,
    sortable: true,
  },
  {
    id: "fileUrl",
    backendKey: "uploaded_file_url",
    customHeader: "File Link",
    showTooltip: false,
    sortable: true,
    isDownloadable: true,
    maxWidth: 10,
  },
];
export const reconciliationFileValidationCriteria = [
  {
    id: "file-format",
    description: "File must be in CSV format",
    check: (file) => file.type === "text/csv",
  },
  {
    id: "required-columns",
    description: "Contains all required columns",
    fields: ReconciliationPendingOrderFieldsName.map(
      (field) => field.backendKey,
    ),
  },
  {
    id: "missing-values",
    description: "No missing values in required fields",
  },
  {
    id: "data-type",
    description: "Data types are valid for all fields",
  },
  {
    id: "order-id-unique",
    description: "Order IDs must be unique",
  },
];
export const yourBrandsContentMetrics = [
  {
    id: "socialMediaMetrics",
    backendKey: "social_media_metrics",
    customHeader: "Social Media Metrics",
    label: "Social Media Metrics",
  },
  {
    id: "taggedProductAndClicks",
    backendKey: "tagged_product_and_clicks",
    customHeader: "Tagged Product and Clicks",
    label: "Tagged Product and Clicks",
  },
  {
    id: "salesPerformance",
    backendKey: "sales_performance",
    customHeader: "Sales Performance",
    label: "Sales Performance",
  },
];
export const yourBrandsContentMetricTableColumnsMapping = {
  socialMediaMetrics: [
    {
      id: "content",
      backendKey: "content",
      label: "Content",
      sortable: true,
      showTooltip: true,
      isClickable: true,
    },
    {
      id: "username",
      backendKey: "username",
      label: "Username",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "profile",
      backendKey: "profile",
      label: "Profile",
      sortable: true,
      showTooltip: true,
      isClickable: true,
    },
    {
      id: "posted_on",
      backendKey: "posted_on",
      label: "Posted On",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "views",
      backendKey: "views",
      label: "Views",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "reach",
      backendKey: "reach",
      label: "Reach",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "likes",
      backendKey: "likes",
      label: "Likes",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "comments",
      backendKey: "comments",
      label: "Comments",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "shares",
      backendKey: "shares",
      label: "Shares",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "saves",
      backendKey: "saves",
      label: "Saves",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "engage_dms_sent",
      backendKey: "engage_dms_sent",
      label: "Engage DMs Sent",
      sortable: true,
      showTooltip: true,
    },
  ],
  taggedProductAndClicks: [
    {
      id: "content",
      backendKey: "content",
      label: "Content",
      sortable: true,
      showTooltip: true,
      isClickable: true,
    },
    {
      id: "brands_tagged",
      backendKey: "brands_tagged",
      label: "Brands Tagged",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "products_tagged",
      backendKey: "products_tagged",
      label: "Products Tagged",
      sortable: true,
      showTooltip: true,
    },
    // { id: 'clicks', backendKey: 'clicks', label: 'Clicks', sortable: true, showTooltip: true },
    // { id: 'unique_clicks', backendKey: 'unique_clicks', label: 'Unique Clicks', sortable: true, showTooltip: true },
    {
      id: "products_tagged_your_brand",
      backendKey: "products_tagged_your_brand",
      label: "Products Tagged (Your Brand)",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "clicks_your_brand",
      backendKey: "clicks_your_brand",
      label: "Clicks (Your Brand)",
      sortable: true,
      showTooltip: true,
    },
    // { id: 'unique_clicks_your_brand', backendKey: 'unique_clicks_your_brand', label: 'Unique Clicks (Your Brand)', sortable: true, showTooltip: true },
  ],
  salesPerformance: [
    {
      id: "content",
      backendKey: "content",
      label: "Content",
      sortable: true,
      showTooltip: true,
      isClickable: true,
    },
    // { id: 'orders_total', backendKey: 'orders_total', label: 'Orders (Total)', sortable: true, showTooltip: true },
    // { id: 'gmv_total', backendKey: 'gmv_total', label: 'GMV (Total)', sortable: true, showTooltip: true },
    {
      id: "orders_your_brand",
      backendKey: "orders_your_brand",
      label: "Orders (Your Brand)",
      sortable: true,
      showTooltip: true,
    },
    {
      id: "gmv_your_brand",
      backendKey: "gmv_your_brand",
      label: "GMV (Your Brand)",
      sortable: true,
      showTooltip: true,
    },
  ],
};
export const yourBrandsCreatorsPerformanceFieldsName = [
  {
    id: "username",
    backendKey: "username",
    customHeader: "Username",
    label: "Username",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "profile",
    backendKey: "profile",
    customHeader: "Creator Profile",
    label: "Creator Profile",
    sortable: false,
    showTooltip: true,
    isClickable: true,
  },
  {
    id: "followers",
    backendKey: "follower_count",
    label: "Followers",
    customHeader: "Followers",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "engage",
    backendKey: "engage",
    label: "Engage Active (T/F)",
    customHeader: "Engage",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "clicks",
    backendKey: "total_clicks",
    label: "Clicks",
    customHeader: "Clicks",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "orders",
    backendKey: "total_orders",
    label: "Orders",
    customHeader: "Orders",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "gmv",
    backendKey: "gmv",
    label: "GMV",
    customHeader: "GMV (₹)",
    sortable: true,
    showTooltip: true,
    isCurrency: true,
  },
];
export const creatorStatusOnWishlinkFieldsName = [
  {
    id: "username",
    backendKey: "username",
    customHeader: "Username",
    label: "Username",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "followers",
    backendKey: "follower_count",
    label: "Followers",
    customHeader: "Followers",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "on_wl_since",
    backendKey: "on_wl_since",
    label: "on WL Since",
    customHeader: "on WL Since",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "engage_status",
    backendKey: "engage_status",
    label: "Engage Active (T/F)",
    customHeader: "Engage Status",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "total_posts_on_wl",
    backendKey: "total_posts_on_wl",
    label: "Total Posts On WL",
    customHeader: "Total Posts On WL",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "total_sales_on_wl",
    backendKey: "total_sales_on_wl",
    label: "Total Sales(₹) on WL",
    customHeader: "Total Sales(₹) on WL",
    sortable: true,
    showTooltip: true,
    isCurrency: true,
  },
];
export const yourAllCampaignsFieldsName = [
  {
    id: "campaignid",
    backendKey: "campaign_id",
    label: "Campaign Name",
    sortable: true,
    showTooltip: true,
    isClickable: true,
    isInternalLink: true,
  },
  {
    id: "created_on",
    backendKey: "created_on",
    label: "Created On",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "views",
    backendKey: "views",
    label: "Views",
    sortable: true,
    showTooltip: true,
    formatterType: "number",
  },
  {
    id: "clicks",
    backendKey: "clicks",
    label: "Clicks",
    customHeader: "Clicks",
    sortable: true,
    showTooltip: true,
    formatterType: "number",
  },
  {
    id: "engagement_rate",
    backendKey: "engagement_rate",
    label: "Engagement Rate (%)",
    customHeader: "Engagement Rate (%)",
    sortable: true,
    showTooltip: true,
    isCurrency: true,
    formatterType: "decimal",
  },
  {
    id: "cpv",
    backendKey: "CPV",
    label: "CPV",
    customHeader: "Cost Per View",
    sortable: true,
    showTooltip: true,
    formatterType: "decimal",
  },
  {
    id: "cpe",
    backendKey: "CPE",
    label: "CPE",
    customHeader: "Cost Per Engagement",
    sortable: true,
    showTooltip: true,
    formatterType: "decimal",
  },
  {
    id: "cpc",
    backendKey: "CPC",
    label: "CPC",
    customHeader: "Cost Per Click",
    sortable: true,
    showTooltip: true,
    formatterType: "decimal",
  },
];
export const campaignCreateEditContentPiecesRequiredFieldsName = [
  "Content Url",
  "Promoted Product Name",
  "Cost",
];
export const campaignCreateEditContentPiecesDisplayFieldsName = [
  "Content Url *",
  "Promoted Product Name",
  "Cost",
];
export const catalogUploadProductSkusFieldsName = [
  "Product Name",
  "Marketplace",
  "SKU ID",
];
export const catalogUploadSupportedMarketplace = [
  "Amazon",
  "Flipkart",
  "Myntra",
  "Tira",
];
export const campaignContentwiseFunnelMetrics = [
  {
    id: "socialMediaMetrics",
    backendKey: "social_media_metrics",
    customHeader: "Social Media Metrics",
    label: "Social Media Metrics",
  },
  {
    id: "taggedProductAndClicks",
    backendKey: "tagged_product_and_clicks",
    customHeader: "Tagged Product and Clicks",
    label: "Tagged Product and Clicks",
  },
  {
    id: "salesPerformance",
    backendKey: "sales_performance",
    customHeader: "Sales Performance",
    label: "Sales Performance",
  },
  {
    id: "brandPerformance",
    backendKey: "brand_performance",
    customHeader: "Brand Performance",
    label: "Brand Performance",
  },
  {
    id: "promotedProductPerformance",
    backendKey: "promoted_product_performance",
    customHeader: "Promoted Product Performance",
    label: "Promoted Product Performance",
  },
];
// export const campaignContentwiseFunnelMetricTableColumnsMapping = {
//     socialMediaMetrics: [
//         { id: 'content', backendKey: 'content', label: 'Content', sortable: true, showTooltip: true, isClickable: true },
//         { id: 'username', backendKey: 'username', label: 'Username', sortable: true, showTooltip: true },
//         { id: 'profile', backendKey: 'profile', label: 'Profile', sortable: true, showTooltip: true },
//         { id: 'posted_on', backendKey: 'posted_on', label: 'Posted On', sortable: true, showTooltip: true },
//         { id: 'views', backendKey: 'views', label: 'Views', sortable: true, showTooltip: true },
//         { id: 'reach', backendKey: 'reach', label: 'Reach', sortable: true, showTooltip: true },
//         { id: 'likes', backendKey: 'likes', label: 'Likes', sortable: true, showTooltip: true },
//         { id: 'comments', backendKey: 'comments', label: 'Comments', sortable: true, showTooltip: true },
//         { id: 'shares', backendKey: 'shares', label: 'Shares', sortable: true, showTooltip: true },
//         { id: 'saves', backendKey: 'saves', label: 'Saves', sortable: true, showTooltip: true },
//         { id: 'engage_dms_sent', backendKey: 'engage_dms_sent', label: 'Engage DMs Sent', sortable: true, showTooltip: true },
//     ],
//     taggedProductAndClicks: [
//         { id: 'content', backendKey: 'content', label: 'Content', sortable: true, showTooltip: true, isClickable: true },
//         { id: 'brands_tagged', backendKey: 'brands_tagged', label: 'Brands Tagged', sortable: true, showTooltip: true },
//         { id: 'products_tagged', backendKey: 'products_tagged', label: 'Products Tagged', sortable: true, showTooltip: true },
//         { id: 'clicks', backendKey: 'clicks', label: 'Clicks', sortable: true, showTooltip: true },
//         { id: 'products_tagged_brand_wise', backendKey: 'brand_wise_products', label: 'Products Tagged (Brand Wise)', sortable: true, showTooltip: true, formatterType: 'json' },
//         { id: 'clicks_brand_wise', backendKey: 'brand_wise_clicks', label: 'Clicks (Brand Wise)', sortable: true, showTooltip: true, formatterType: 'json' },
//     ],
//     salesPerformance: [
//         { id: 'content', backendKey: 'content', label: 'Content', sortable: true, showTooltip: true, isClickable: true },
//         { id: 'orders_total', backendKey: 'orders_total', label: 'Orders (Total)', sortable: true, showTooltip: true },
//         { id: 'gmv_total', backendKey: 'gmv_total', label: 'GMV (Total)', sortable: true, showTooltip: true, formatterType: 'number' },
//         { id: 'orders_brand_wise', backendKey: 'brand_wise_orders', label: 'Orders (Brand Wise)', sortable: true, showTooltip: true, formatterType: 'json', },
//         { id: 'gmv_brand_wise', backendKey: 'brand_wise_gmv', label: 'GMV (Brand Wise)', sortable: true, showTooltip: true, formatterType: 'json', },
//     ],
//     brandPerformance: [
//         { id: 'content',                 backendKey: 'content',                  label: 'Content',                         sortable: true, showTooltip: true, isClickable: true },
//         { id: 'ordersOnFlipkartBrand',   backendKey: 'orders_on_flipkart_brand', label: 'Orders on Flipkart (Your Brand)', sortable: true, showTooltip: true, condition: 'flipkart_skus' },
//         { id: 'gmvOnFlipkartBrand',      backendKey: 'gmv_on_flipkart_brand',    label: 'GMV on Flipkart (Your Brand)',    sortable: true, showTooltip: true, formatterType: 'number', condition: 'flipkart_skus' },
//         { id: 'ordersOnMyntraBrand',     backendKey: 'orders_on_myntra_brand',   label: 'Orders on Myntra (Your Brand)',   sortable: true, showTooltip: true, condition: 'myntra_skus' },
//         { id: 'gmvOnMyntraBrand',        backendKey: 'gmv_on_myntra_brand',      label: 'GMV on Myntra (Your Brand)',      sortable: true, showTooltip: true, formatterType: 'number', condition: 'myntra_skus' },
//         { id: 'ordersOnTiraBrand',       backendKey: 'orders_on_tira_brand',     label: 'Orders on Tira (Your Brand)',     sortable: true, showTooltip: true, condition: 'tira_skus' },
//         { id: 'gmvOnTiraBrand',          backendKey: 'gmv_on_tira_brand',        label: 'GMV on Tira (Your Brand)',        sortable: true, showTooltip: true, formatterType: 'number', condition: 'tira_skus' },
//     ],
//     promotedProductPerformance: [
//         { id: 'content',                                backendKey: 'content',                                  label: 'Content',                                sortable: true, showTooltip: true, isClickable: true },
//         { id: 'ordersOnFlipkartPromotedProducts',       backendKey: 'orders_on_flipkart_promoted_products',     label: 'Orders on Flipkart (Promoted Products)', sortable: true, showTooltip: true, formatterType: 'number', condition: 'flipkart_skus'},
//         { id: 'gmvOnFlipkartPromotedProducts',          backendKey: 'gmv_on_flipkart_promoted_products',        label: 'GMV on Flipkart (Promoted Products)',    sortable: true, showTooltip: true, formatterType: 'number', condition: 'flipkart_skus'},
//         { id: 'ordersOnMyntraPromotedProducts',         backendKey: 'orders_on_myntra_promoted_products',       label: 'Orders on Myntra (Promoted Products)',   sortable: true, showTooltip: true, formatterType: 'number', condition: 'myntra_skus'},
//         { id: 'gmvOnMyntraPromotedProducts',            backendKey: 'gmv_on_myntra_promoted_products',          label: 'GMV on Myntra (Promoted Products)',      sortable: true, showTooltip: true, formatterType: 'number', condition: 'myntra_skus'},
//         { id: 'ordersOnTiraPromotedProducts',           backendKey: 'orders_on_tira_promoted_products',         label: 'Orders on Tira (Promoted Products)',     sortable: true, showTooltip: true, formatterType: 'number', condition: 'tira_skus'},
//         { id: 'gmvOnTiraPromotedProducts',              backendKey: 'gmv_on_tira_promoted_products',            label: 'GMV on Tira (Promoted Products)',        sortable: true, showTooltip: true, formatterType: 'number', condition: 'tira_skus'},
//     ],
// };

// Centralized condition functions
export const campaignContentwiseFunnelConditionFunctions = {};

export const tableActions = {
  openInstagram: (username) => {
    if (username) {
      window.open(
        `https://instagram.com/${username}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  },
  toggleColumns: (toggleSwitch, columns, setColumns) => {
    // Toggle the hideOnUi property for columns with the same toggleSwitch value
    const updatedColumns = columns.map((col) => {
      if (col.toggleSwitch === toggleSwitch) {
        return { ...col, hideOnUi: !col.hideOnUi };
      }
      return col;
    });

    setColumns(updatedColumns);
  },
};

const customTooltipForNotOnWL = (value, row) => {
  if (row?.is_wishlink_post === false) {
    return `Please check once with the creator if they have added their post to Wishlink. Once added, these metrics would reflect here`;
  }
  return value;
};

const customTooltipForNA = (value, row) => {
  if (value === "NA") {
    return "Upload SKU IDs to track sales for marketplace, or switch to another platform with better data coverage";
  }
  return value;
};

export const campaignContentwiseFunnelMetricTableColumnsMapping = [
  {
    id: "content",
    backendKey: "content",
    label: "Content",
    sortable: true,
    showTooltip: true,
    isClickable: true,
  },
  {
    id: "username",
    backendKey: "username",
    label: "Username",
    customTooltip: customTooltipForNotOnWL,
    sortable: true,
    showTooltip: true,
    action: "openInstagram",
    headerTooltipValue: "Click to open Instagram profile",
  },
  {
    id: "profile",
    backendKey: "profile",
    label: "Profile",
    sortable: true,
    showTooltip: true,
    isClickable: true,
    hideOnUi: true,
    customTooltip: customTooltipForNotOnWL,
  },
  {
    id: "posted_on",
    backendKey: "posted_on",
    customTooltip: customTooltipForNotOnWL,
    label: "Posted On",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "views",
    backendKey: "views",
    label: "Views",
    sortable: true,
    showTooltip: true,
    customTooltip: customTooltipForNotOnWL,
  },
  {
    id: "engagement_rate",
    backendKey: "engagement_rate",
    label: "Engagement Rate %",
    sortable: false,
    showTooltip: true,
    headerAction: "toggleColumns",
    formatterType: "decimal",
    customTooltip: customTooltipForNotOnWL,
  },
  {
    id: "likes",
    backendKey: "likes",
    label: "Likes",
    sortable: true,
    customTooltip: customTooltipForNotOnWL,
    showTooltip: true,
    toggleSwitch: "engagement_rate",
    hideOnUi: true,
  },
  {
    id: "comments",
    backendKey: "comments",
    label: "Comments",
    sortable: true,
    showTooltip: true,
    toggleSwitch: "engagement_rate",
    hideOnUi: true,
    customTooltip: customTooltipForNotOnWL,
  },
  {
    id: "shares",
    backendKey: "shares",
    label: "Shares",
    sortable: true,
    showTooltip: true,
    customTooltip: customTooltipForNotOnWL,
    toggleSwitch: "engagement_rate",
    hideOnUi: true,
  },
  {
    id: "saves",
    backendKey: "saves",
    label: "Saves",
    sortable: true,
    showTooltip: true,
    toggleSwitch: "engagement_rate",
    customTooltip: customTooltipForNotOnWL,
    hideOnUi: true,
  },
  {
    id: "engage_dm_sent",
    backendKey: "engage_dm_sent",
    label: "Engage DMs",
    customTooltip: customTooltipForNotOnWL,
    description: "Auto-DMs sent on comments with purchase intent",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "clicks_your_brand",
    backendKey: "clicks_your_brand",
    label: "Clicks",
    customTooltip: customTooltipForNotOnWL,
    sortable: true,
    showTooltip: true,
  },
  {
    id: "orders_your_brand",
    backendKey: "orders_your_brand",
    label: "Products Sold*",
    sortable: true,
    showTooltip: true,
    customTooltip: customTooltipForNA,
  },
  {
    id: "gmv_your_brand",
    backendKey: "gmv_your_brand",
    label: "Sales(₹)*",
    sortable: true,
    showTooltip: true,
    formatterType: "number",
    formatterOverride: "NA",
    customTooltip: customTooltipForNA,
  },
  {
    id: "quantity_by_sku",
    backendKey: "quantity_by_sku",
    label: "Product wise orders*",
    sortable: true,
    showTooltip: true,
    formatterType: "json",
    formatterOverride: "NA",
    customTooltip: customTooltipForNA,
  },
  {
    id: "gmv_by_sku",
    backendKey: "gmv_by_sku",
    label: "Product wise sales(₹)*",
    sortable: true,
    showTooltip: true,
    formatterType: "json",
    formatterOverride: "NA",
    customTooltip: customTooltipForNA,
  },
  {
    id: "wishlink_post",
    backendKey: "wishlink_post",
    label: "Wishlink Post",
    sortable: false,
    showTooltip: false,
    formatterType: "string",
    formatterOverride: "Yes",
    customTooltip: customTooltipForNotOnWL,
    onlyForCSV: true,
  },
];

// export const amazonNonAmazonCommonContentMetrics = ['socialMediaMetrics', 'taggedProductAndClicks'];
// export const campaignCreatorwiseAmazonFunnelMetricTableColumnsMapping = {
//     salesPerformance: [
//         { id: 'creator', backendKey: 'creator', label: 'Creator', sortable: true, showTooltip: true },
//         { id: 'gmvOnAmazon', backendKey: 'gmv_on_amazon', label: 'GMV (on Amazon)', sortable: true, showTooltip: true, formatterType: 'number',},
//     ],
//     brandPerformance: [
//         { id: 'creator',                backendKey: 'creator',                   label: 'Creator',                       sortable: true, showTooltip: true },
//         { id: 'gmvOnAmazonBrand',       backendKey: 'gmv_on_amazon_brand',       label: 'GMV on Amazon(Your Brand)',     sortable: true, showTooltip: true, formatterType: 'number', },
//     ],
//     promotedProductPerformance: [
//         { id: 'creator',                        backendKey: 'creator',                            label: 'Creator',                              sortable: true, showTooltip: true },
//         { id: 'gmvOnAmazonPromotedProducts',    backendKey: 'gmv_on_amazon_promoted_products',    label: 'GMV on Amazon (Promoted Products)',    sortable: true, showTooltip: true, formatterType: 'number' },
//     ],
// };
export const SkuUploadValidationCriteria = [
  {
    id: "file-format",
    label: "File Format",
    description: "The uploaded file must be a non-empty valid CSV file",
  },
  {
    id: "required-columns",
    label: "Missing Required Columns",
    description: `The file must contain the following columns: ${catalogUploadProductSkusFieldsName.join(", ")}`,
  },
  {
    id: "marketplace-supported",
    label: "Unsupported Marketplace",
    description: `Supported marketplaces are: ${catalogUploadSupportedMarketplace.join(", ")}`,
  },
  {
    id: "missing-values",
    label: "Missing Values",
    description: "No missing values in required fields",
  },
];
export const CampaignUploadContentPiecesValidationCriteria = [
  {
    id: "file-format",
    label: "File Format",
    description: "The uploaded file must be a non-empty valid CSV file",
  },
  {
    id: "required-columns",
    label: "Missing Required Columns",
    description: "The file must contain the following column: Content Url",
  },
  {
    id: "content-url-invalid",
    label: "Invalid Content Url",
    description: `Should have valid content URL`,
  },
  {
    id: "missing-values",
    label: "Missing Values",
    description: "No missing values in required fields",
  },
];
export const CampaignUploadSkuIdsValidationCriteria = [
  {
    id: "file-format",
    label: "File Format",
    description: "The uploaded file must be a non-empty valid CSV file",
  },
  {
    id: "required-columns",
    label: "Missing Required Columns",
    description: `The file must contain the following columns: ${catalogUploadProductSkusFieldsName.join(", ")}`,
  },
  {
    id: "marketplace-supported",
    label: "Unsupported Marketplace",
    description: `Supported marketplaces are: ${catalogUploadSupportedMarketplace.join(", ")}`,
  },
  {
    id: "missing-values",
    label: "Missing Values",
    description: "No missing values in required fields",
  },
];

export const catalogMarketplaceCurrentSkus = [
  {
    id: "productname",
    backendKey: "product_name",
    label: "Product Name",
    sortable: true,
    showTooltip: true,
  },
  {
    id: "marketplace",
    backendKey: "marketplace",
    label: "Marketplace",
    sortable: true,
    showTooltip: true,
    filterable: true,
  },
  {
    id: "skuid",
    backendKey: "sku_id",
    label: "SKU ID",
    sortable: true,
    showTooltip: true,
  },
];
export const UserRoleOptions = [
  { id: "Admin", value: "Admin", label: "Admin" },
  { id: "Member", value: "Member", label: "Member" },
];
export const UsersTableColumnsFields = [
  {
    id: "name",
    header: "Name",
    accessor: "name",
    width: "auto",
  },
  {
    id: "email",
    header: "Email",
    accessor: "email",
    width: "40%",
  },
  {
    id: "role",
    header: "Role",
    accessor: "role",
    width: "auto",
  },
  {
    id: "status",
    header: "Status",
    accessor: "status",
    width: "auto",
  },
  {
    id: "actions",
    header: "Actions",
    isAction: true,
    width: "10%",
  },
];

export const GraphXAxisKeyName = "date";
export const TIME_PERIOD_KEY = "periodToDate"; // Represents Month-to-Date, Week-to-Date formats
export const DATE_RANGE_KEY = "startEndDate";
export const DEFAULT_SALE_DATA_ORDER_BY_COLUMN = "sale_date";
export const DEFAULT_CONTENT_DATA_ORDER_BY_COLUMN = "posted_on";
export const DEFAULT_CONTENT_TRENDS_ORDER_BY_COLUMN = "date";
export const DEFAULT_SALE_TRENDS_ORDER_BY_COLUMN = "date";
export const DEFAULT_NMV_TRENDS_ORDER_BY_COLUMN = "date";
export const DEFAULT_ORDER_STATUS_UPLOADS_ORDER_BY_COLUMN = "id";
export const DEFAULT_ORDER_STATUS_PENDING_ORDER_BY_COLUMN = "sale_date";
export const DEFAULT_YOUR_BRANDS_CONTENT_DATA_ORDER_BY_COLUMN = "posted_on";
export const DEFAULT_YOUR_BRAND_CREATORS_PERFORMANCE_ORDER_BY_COLUMN =
  "username";
export const DEFAULT_CHECK_CREATOR_STATUS_LIST_ORDER_BY_COLUMN = "username";
export const DEFAULT_YOUR_ALL_CAMPAIGNS_ORDER_BY_COLUMN = "created_on";
export const DEFAULT_CAMPAIGN_CONTENT_WISE_TABLE_ORDER_BY_COLUMN = "posted_on";
export const DEFAULT_CAMPAIGN_CREATOR_WISE_TABLE_ORDER_BY_COLUMN = "creator";
export const DEFAULT_CATALOG_CURRENT_SKUS_ORDER_BY_COLUMN = "sku_id";
export const DEFAULT_SORT_DIRECTION = "desc";
export const DEFAULT_TRENDS_COMPARISON_PERIOD = {
  id: "dod",
  label: "DAY-ON-DAY",
  value: "dod",
  backendKey: "day_on_day",
};
export const SIDEBAR_CLOSED_WIDTH = "60px";
export const SIDEBAR_OPENED_WIDTH = "210px";
export const APPBAR_HEIGHT = "80px";
/** V2 dashboard shell (`TopNavbar` in `V2DashboardLayout`); fixed drawers must align to this, not legacy `APPBAR_HEIGHT`. */
export const DASHBOARD_V2_TOP_NAVBAR_HEIGHT = 80;
export const SALES_OVERVIEW_SECTION_NAME = "SALES_OVERVIEW";
export const SALES_TRENDS_SECTION_NAME = "SALES_TRENDS";
export const SALES_RAW_DATA_SECTION_NAME = "SALES_RAW_DATA";
export const CONTENT_OVERVIEW_SECTION_NAME = "CONTENT_OVERVIEW";
export const CONTENT_TRENDS_SECTION_NAME = "CONTENT_TRENDS";
export const CONTENT_RAW_DATA_SECTION_NAME = "CONTENT_RAW_DATA";
export const NMV_OVERVIEW_SECTION_NAME = "NMV_OVERVIEW";
export const NMV_TRENDS_SECTION_NAME = "NMV_TRENDS";
export const SHARE_CONFIRMED_PENDING_ORDER_DATA_SECTION_NAME =
  "PENDING_ORDER_DATA";
export const SHARE_CONFIRMED_PREVIOUS_UPLOADS_SECTION_NAME = "PREVIOUS_UPLOADS";
export const ALL_CONTENT_FUNNEL_YOUR_BRANDS_CONTENT =
  "CONTENT_FUNNEL_YOUR_BRANDS_CONTENT";
export const YOUR_BRANDS_CREATORS_AND_PERFORMANCE = "YOUR_CREATORS_PERFORMANCE";
export const CAMPAIGN_CONTENTWISE_FUNNEL_SECTION_NAME =
  "CAMPAIGN_CONTENTWISE_FUNNEL_SECTION";
export const CATALOG_DOWNLOAD_CURRENT_SKUS = "CATALOG_DOWNLOAD_CURRENT_SKUS";

export const IMAGE_PREFIX = "https://images.wishlink.com/alabaster/";
export const WISHLINK_LOGO_URL = `${IMAGE_PREFIX}wishlink_logo.jpg`;
export const WISHLINK_AUTH_WORDMARK_LOGO_URL =
  "https://images.wishlink.com/alabaster/wishlink_media_1781604849.png";
export const WISHLINK_LOGIN_PAGE_LOGO = `${IMAGE_PREFIX}wishlink_name_orange.png`;
export const WISHLINK_SIDEBAR_LOGO_URL = `${IMAGE_PREFIX}WishlinkLogo-saas.png`;
export const WISHLINK_LOGIN_PAGE_CREATORS_IMAGE = `${IMAGE_PREFIX}wishlink_brand_x_creator_image.png`;
export const BRANDS_COLLAGE_IMAGE = `${IMAGE_PREFIX}wl_brands_logo.png`;

export const SUPPORT_EMAIL_ID = "brandits@wishlink.com";
export const PRIVACY_POLICY_LINK = "https://www.wishlink.com/w/privacy-policy";
export const TERMS_OF_SERVICE_LINK =
  "https://www.wishlink.com/w/terms-of-service";
export const COOKIE_POLICY_LINK = "https://www.wishlink.com/w/cookie-policy";
export const ONBOARDING_SUPPORT_EMAIL_ID = "influenceos@wishlink.com";
export const ONBOARDING_CALENDLY_LINK =
  "https://calendly.com/vinayak-rastogi-wishlink/30min";

export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\W_]{8,}$/;
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const CAMPAIGN_NAME_REGEX = /^[A-Za-z0-9_]+$/;
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
export const LOCALHOST_DOMAINS = ["localhost", "127.0.0.1"];

export const TABLE_ROWS_PER_PAGE_OPTIONS = [10, 50, 100];
export const DEFAULT_ROWS_PER_PAGE_SELECTED = 10;

export const DEFAULT_ADMIN_SELECTED_BRAND = {
  id: 51,
  label: "Innovist",
  value: "Innovist",
};
export const DEFAULT_ADMIN_SELECTED_ONBOARDING_BRAND = null;
export const ADMIN_USER_TYPE = "adminUser";
export const EMAIL_USER_TYPE = "emailUser";
export const UPPER_SECTION_TAB_LABEL = "UPPER_SECTION_TAB";
export const LOWER_SECTION_TAB_LABEL = "LOWER_SECTION_TAB";
export const FOOTER_SECTION_TAB_LABEL = "FOOTER_SECTION_TAB";
export const HEADER_PROFILE_DROPDOWN = "HEADER_PROFILE_DROPDOWN";
export const APPROVED_USER_STATUS = "APPROVED";
export const PENDING_USER_STATUS = "PENDING";
export const ONBOARDING_USER_STATUS = "ONBOARDING";
export const APPROVED_THROUGH_DIY_FLOW_USER_STATUS = "APPROVED_VIA_DIY";
export const USER_IN_REVIEW = "IN_REVIEW"; // TODO: Change to IN_REVIEW when backend is updated
export const USER_IS_WAITLISTED = "WAITLISTED"; // TODO: Change to WAITLISTED when backend is updated

export const ONBOARDING_APPLICATION_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  CHANGES_REQUESTED: "changes_requested",
};

export const BRAND_PROFILE_PRIMARY = "#EE4D37";

// Campaign request budget options, in the Discover filters' style: the value is a slab key and the
// backend looks up its numbers (BUDGET_SLABS in campaign_request_constants.py) and stores the lower bound.
export const CAMPAIGN_BUDGET_OPTIONS = [
  { value: "0-100000", label: "Under ₹1L" },
  { value: "100000-500000", label: "₹1–5L" },
  { value: "500000-1500000", label: "₹5–15L" },
  { value: "1500000-5000000", label: "₹15–50L" },
  { value: "5000000+", label: "₹50L+" },
];

// One category list for signup and Profile -> Brand info. Keep in sync with BRAND_CATEGORIES in the
// backend's brand_dashboard/services/users/brand_info_service.py.
export const BRAND_CATEGORIES = [
  "Apparel",
  "Footwear",
  "Accessories",
  "Personal Care",
  "Home",
  "Electronics",
  "Sporting Goods",
  "Pet Supplies",
  "Groceries",
  "Toys and Games",
];

export const BRAND_PROFILE_MARKETPLACE_OPTIONS = [
  "Amazon",
  "Flipkart",
  "Myntra",
  "Nykaa",
  "Tira",
];

export const BRAND_PROFILE_GMV_RANGES = [
  "< ₹10 Lakhs",
  "₹10 - ₹50 Lakhs",
  "₹50 Lakhs - ₹1 Crore",
  "₹1 - ₹5 Crore",
  "₹5 - ₹20 Crore",
  "> ₹20 Crore",
];

export const BRAND_PROFILE_INFLUENCER_COUNTS = [
  "< 5 influencers",
  "5 - 10 influencers",
  "10 - 50 influencers",
  "50 - 200 influencers",
  "> 200 influencers",
];

// Initial filter values for creator filters
export const INITIAL_CREATOR_FILTERS = {
  creator_filters: {
    engagement_buckets: [],
    gender: [],
    follower_ranges: [],
    views_ranges: [],
    engagement_rate_ranges: [],
    accounts_engaged_ranges: [],
    keywords: [],
    age_demographics: {},
    region_demographics: {},
    city_tier_demographics: {},
    audience_gender_demographics: {},
    categories: [],
    subcategories: [],
    sub_subcategory: [],
    languages: [],
    username: "",
  },
  product_filters: {
    brand_names: [],
  },
  sort_by: [
    {
      field: "total_sales", //followers,no_of_posts,median_views,total_clicks,conversion_rate,total_sales,aov
      order: "desc",
    },
  ],
  pagination: {
    page: 1,
    rows_per_page: 50,
  },
};

export const CATEGORIES_WITH_SUBCATEGORIES = [
  {
    label: "Home",
    value: "Home",
    subcategories: [
      { label: "Kitchen and Dining", value: "Kitchen and Dining" },
      { label: "Home Decor", value: "Home Decor" },
      { label: "Home Furnishing", value: "Home Furnishing" },
      { label: "Kitchen Linen", value: "Kitchen Linen" },
      { label: "Bath", value: "Bath" },
      { label: "Bedding", value: "Bedding" },
      { label: "Home Organisers", value: "Home Organisers" },
      { label: "Floor Covering", value: "Floor Covering" },
      { label: "Furniture", value: "Furniture" },
      { label: "Home Appliances", value: "Home Appliances" },
    ],
  },
  {
    label: "Personal Care",
    value: "Personal Care",
    subcategories: [
      { label: "Makeup", value: "Makeup" },
      { label: "Bath and Body", value: "Bath and Body" },
      { label: "Skin Care", value: "Skin Care" },
      { label: "Mom and Baby", value: "Mom and Baby" },
      { label: "Mens Grooming", value: "Mens Grooming" },
      { label: "Beauty Accessories", value: "Beauty Accessories" },
      { label: "Wellness", value: "Wellness" },
      { label: "Fragrance", value: "Fragrance" },
      { label: "Hair", value: "Hair" },
      { label: "Health And Wellbeing", value: "Health And Wellbeing" },
      { label: "Appliances", value: "Appliances" },
    ],
  },
  {
    label: "Accessories",
    value: "Accessories",
    subcategories: [
      { label: "Baby Utilities", value: "Baby Utilities" },
      { label: "Accessories", value: "Accessories" },
      { label: "Jewellery", value: "Jewellery" },
      { label: "Bags", value: "Bags" },
      { label: "Precious Jewellery", value: "Precious Jewellery" },
      { label: "Watches", value: "Watches" },
      { label: "Eyewear", value: "Eyewear" },
      { label: "Shoe Accessories", value: "Shoe Accessories" },
      { label: "Headwear", value: "Headwear" },
      { label: "Camping", value: "Camping" },
      { label: "Sports Accessories", value: "Sports Accessories" },
      { label: "Cufflinks", value: "Cufflinks" },
      { label: "Mufflers", value: "Mufflers" },
      { label: "Scarves", value: "Scarves" },
      { label: "Wallets", value: "Wallets" },
      { label: "Gloves", value: "Gloves" },
      { label: "Stoles", value: "Stoles" },
      { label: "Belts", value: "Belts" },
      { label: "Socks", value: "Socks" },
      { label: "Pens", value: "Pens" },
      { label: "Ties", value: "Ties" },
    ],
  },
  {
    label: "Apparel",
    value: "Apparel",
    subcategories: [
      { label: "Topwear", value: "Topwear" },
      { label: "Bottomwear", value: "Bottomwear" },
      { label: "Apparel Set", value: "Apparel Set" },
      { label: "Innerwear", value: "Innerwear" },
      { label: "Loungewear and Nightwear", value: "Loungewear and Nightwear" },
      { label: "Dress", value: "Dress" },
      { label: "Saree", value: "Saree" },
    ],
  },
  {
    label: "Pet Supplies",
    value: "Pet Supplies",
    subcategories: [
      { label: "Pet Grooming", value: "Pet Grooming" },
      { label: "Pet Furniture", value: "Pet Furniture" },
      { label: "Pet Accessories", value: "Pet Accessories" },
      { label: "Dog Clothing", value: "Dog Clothing" },
      { label: "Pet Food Accessories", value: "Pet Food Accessories" },
      { label: "Pet Toys", value: "Pet Toys" },
    ],
  },
  {
    label: "Gourmet",
    value: "Gourmet",
    subcategories: [
      { label: "Gourmet Gift Set", value: "Gourmet Gift Set" },
      { label: "Sports Nutrition", value: "Sports Nutrition" },
      { label: "Pantry", value: "Pantry" },
      { label: "Confectionery", value: "Confectionery" },
      { label: "Beverages", value: "Beverages" },
      { label: "Snacks", value: "Snacks" },
    ],
  },
  {
    label: "Footwear",
    value: "Footwear",
    subcategories: [
      { label: "Shoes", value: "Shoes" },
      { label: "Sandal", value: "Sandal" },
      { label: "Flip Flops", value: "Flip Flops" },
    ],
  },
  {
    label: "Sporting Goods",
    value: "Sporting Goods",
    subcategories: [
      { label: "Sports Equipment", value: "Sports Equipment" },
      { label: "Workout Accessories", value: "Workout Accessories" },
    ],
  },
  {
    label: "Toys and Games",
    value: "Toys and Games",
    subcategories: [],
  },
  {
    label: "Electronics",
    value: "Electronics",
    subcategories: [
      { label: "Cameras and Photography", value: "Cameras and Photography" },
    ],
  },
  {
    label: "Free Items",
    value: "Free Items",
    subcategories: [{ label: "Free Gifts", value: "Free Gifts" }],
  },
  {
    label: "Gift Cards",
    value: "Gift Cards",
    subcategories: [],
  },
  {
    label: "Gift Cards and Subscriptions",
    value: "Gift Cards and Subscriptions",
    subcategories: [],
  },
  {
    label: "Groceries",
    value: "Groceries",
    subcategories: [],
  },
];

// Keep the old exports for backward compatibility
export const CATEGORIES = [
  {
    label: "Accessories",
    value: "Accessories",
  },
  {
    label: "Apparel",
    value: "Apparel",
  },
  {
    label: "Electronics",
    value: "Electronics",
  },
  {
    label: "Footwear",
    value: "Footwear",
  },
  {
    label: "Free Items",
    value: "Free Items",
  },
  {
    label: "Gift Cards",
    value: "Gift Cards",
  },
  {
    label: "Gift Cards and Subscriptions",
    value: "Gift Cards and Subscriptions",
  },
  {
    label: "Gourmet",
    value: "Gourmet",
  },
  {
    label: "Groceries",
    value: "Groceries",
  },
  {
    label: "Home",
    value: "Home",
  },
  {
    label: "Personal Care",
    value: "Personal Care",
  },
  {
    label: "Pet Supplies",
    value: "Pet Supplies",
  },
  {
    label: "Sporting Goods",
    value: "Sporting Goods",
  },
  {
    label: "Toys and Games",
    value: "Toys and Games",
  },
];

export const SUBCATEGORIES = [
  {
    label: "Accessories",
    value: "Accessories",
  },
  {
    label: "Apparel Set",
    value: "Apparel Set",
  },
  {
    label: "Appliances",
    value: "Appliances",
  },
  {
    label: "Baby Utilities",
    value: "Baby Utilities",
  },
  {
    label: "Bags",
    value: "Bags",
  },
  {
    label: "Bath",
    value: "Bath",
  },
  {
    label: "Bath and Body",
    value: "Bath and Body",
  },
  {
    label: "Beauty Accessories",
    value: "Beauty Accessories",
  },
  {
    label: "Bedding",
    value: "Bedding",
  },
  {
    label: "Belts",
    value: "Belts",
  },
  {
    label: "Beverages",
    value: "Beverages",
  },
  {
    label: "Bottomwear",
    value: "Bottomwear",
  },
  {
    label: "Cameras and Photography",
    value: "Cameras and Photography",
  },
  {
    label: "Camping",
    value: "Camping",
  },
  {
    label: "Cat Clothing",
    value: "Cat Clothing",
  },
  {
    label: "Confectionery",
    value: "Confectionery",
  },
  {
    label: "Cufflinks",
    value: "Cufflinks",
  },
  {
    label: "Dog Clothing",
    value: "Dog Clothing",
  },
  {
    label: "Dress",
    value: "Dress",
  },
  {
    label: "Eyewear",
    value: "Eyewear",
  },
  {
    label: "Flip Flops",
    value: "Flip Flops",
  },
  {
    label: "Floor Covering",
    value: "Floor Covering",
  },
  {
    label: "Fragrance",
    value: "Fragrance",
  },
  {
    label: "Free Gifts",
    value: "Free Gifts",
  },
  {
    label: "Furniture",
    value: "Furniture",
  },
  {
    label: "Gift Cards",
    value: "Gift Cards",
  },
  {
    label: "Gloves",
    value: "Gloves",
  },
  {
    label: "Gourmet Gift Set",
    value: "Gourmet Gift Set",
  },
  {
    label: "Groceries",
    value: "Groceries",
  },
  {
    label: "Hair",
    value: "Hair",
  },
  {
    label: "Headwear",
    value: "Headwear",
  },
  {
    label: "Health And Wellbeing",
    value: "Health And Wellbeing",
  },
  {
    label: "Home Appliances",
    value: "Home Appliances",
  },
  {
    label: "Home Decor",
    value: "Home Decor",
  },
  {
    label: "Home Furnishing",
    value: "Home Furnishing",
  },
  {
    label: "Home Organisers",
    value: "Home Organisers",
  },
  {
    label: "Innerwear",
    value: "Innerwear",
  },
  {
    label: "Jewellery",
    value: "Jewellery",
  },
  {
    label: "Kitchen Linen",
    value: "Kitchen Linen",
  },
  {
    label: "Kitchen and Dining",
    value: "Kitchen and Dining",
  },
  {
    label: "Loungewear and Nightwear",
    value: "Loungewear and Nightwear",
  },
  {
    label: "Makeup",
    value: "Makeup",
  },
  {
    label: "Mens Grooming",
    value: "Mens Grooming",
  },
  {
    label: "Mom and Baby",
    value: "Mom and Baby",
  },
  {
    label: "Mufflers",
    value: "Mufflers",
  },
  {
    label: "Pantry",
    value: "Pantry",
  },
  {
    label: "Pens",
    value: "Pens",
  },
  {
    label: "Pet Accessories",
    value: "Pet Accessories",
  },
  {
    label: "Pet Food Accessories",
    value: "Pet Food Accessories",
  },
  {
    label: "Pet Footwear",
    value: "Pet Footwear",
  },
  {
    label: "Pet Furniture",
    value: "Pet Furniture",
  },
  {
    label: "Pet Grooming",
    value: "Pet Grooming",
  },
  {
    label: "Pet Hygiene",
    value: "Pet Hygiene",
  },
  {
    label: "Pet Toys",
    value: "Pet Toys",
  },
  {
    label: "Power Accessories",
    value: "Power Accessories",
  },
  {
    label: "Precious Jewellery",
    value: "Precious Jewellery",
  },
  {
    label: "Sandal",
    value: "Sandal",
  },
  {
    label: "Saree",
    value: "Saree",
  },
  {
    label: "Scarves",
    value: "Scarves",
  },
  {
    label: "Shoe Accessories",
    value: "Shoe Accessories",
  },
  {
    label: "Shoes",
    value: "Shoes",
  },
  {
    label: "Skin Care",
    value: "Skin Care",
  },
  {
    label: "Smart Wearables",
    value: "Smart Wearables",
  },
  {
    label: "Snacks",
    value: "Snacks",
  },
  {
    label: "Socks",
    value: "Socks",
  },
  {
    label: "Sports Accessories",
    value: "Sports Accessories",
  },
  {
    label: "Sports Equipment",
    value: "Sports Equipment",
  },
  {
    label: "Sports Nutrition",
    value: "Sports Nutrition",
  },
  {
    label: "Stoles",
    value: "Stoles",
  },
  {
    label: "Subscription/Membership",
    value: "Subscription/Membership",
  },
  {
    label: "Tablets and E-Readers",
    value: "Tablets and E-Readers",
  },
  {
    label: "Ties",
    value: "Ties",
  },
  {
    label: "Topwear",
    value: "Topwear",
  },
  {
    label: "Toys and Games",
    value: "Toys and Games",
  },
  {
    label: "Wallets",
    value: "Wallets",
  },
  {
    label: "Watches",
    value: "Watches",
  },
  {
    label: "Wellness",
    value: "Wellness",
  },
  {
    label: "Workout Accessories",
    value: "Workout Accessories",
  },
];

export const GENDER = [
  {
    label: "Male",
    value: "M",
  },
  {
    label: "Female",
    value: "F",
  },
  {
    label: "Unisex",
    value: "O",
  },
];

export const FOLLOWER_RANGES = [
  {
    label: "< 10k",
    value: "0-10000",
  },
  {
    label: "10k - 50k",
    value: "10000-50000",
  },
  {
    label: "50k - 100k",
    value: "50000-100000",
  },
  {
    label: "100k - 250k",
    value: "100000-250000",
  },
  {
    label: "250k - 500k",
    value: "250000-500000",
  },
  {
    label: "> 500k",
    value: "500000+",
  },
];

export const VIEWS_RANGES = [
  {
    label: "<10k",
    value: "0-10000",
  },
  {
    label: "10k - 50k",
    value: "10000-50000",
  },
  {
    label: "50k - 100k",
    value: "50000-100000",
  },
  {
    label: "100k - 250k",
    value: "100000-250000",
  },
  {
    label: "250k - 500k",
    value: "250000-500000",
  },
  {
    label: ">500k",
    value: "500000+",
  },
];

export const ENGAGEMENT_RATE_RANGES = [
  {
    label: "<3%",
    value: "0-3",
  },
  {
    label: "3 - 6%",
    value: "3-6",
  },
  {
    label: "6 - 9%",
    value: "6-9",
  },
  {
    label: "9 - 19%",
    value: "9-19",
  },
  {
    label: ">19%",
    value: "19-100",
  },
];

export const ACCOUNTS_ENGAGED_RANGES = [
  {
    label: "<10k",
    value: "0-10000",
  },
  {
    label: "10k - 50k",
    value: "10000-50000",
  },
  {
    label: "50k - 100k",
    value: "50000-100000",
  },
  {
    label: "100k - 250k",
    value: "100000-250000",
  },
  {
    label: "250k - 500k",
    value: "250000-500000",
  },
  {
    label: ">500k",
    value: "500000+",
  },
];

export const AGE_GROUPS = [
  { label: "13-17", value: "13-17" },
  { label: "18-24", value: "18-24" },
  { label: "25-34", value: "25-34" },
  { label: "35-44", value: "35-44" },
  { label: "45-54", value: "45-54" },
  { label: "55-64", value: "55-64" },
  { label: "65+", value: "65+" },
];

export const AUDIENCE_TIERS = [
  { label: "Tier 1", value: "Tier 1" },
  { label: "Tier 2", value: "Tier 2" },
  { label: "Tier 3", value: "Tier 3" },
];

export const GENDER_OPTIONS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Unisex", value: "UNISEX" },
];

// Values MUST match canonical labels stored in ES `creator_language_list`
// where applicable (see brand_dashboard/utils/creator_discovery.py ).
// BETA: shortlist of commonly-spoken Indian languages.
export const INDIAN_LANGUAGES = [
  { label: "Hindi", value: "Hindi" },
  { label: "English", value: "English" },
  { label: "Telugu", value: "Telugu" },
  { label: "Tamil", value: "Tamil" },
  { label: "Malayalam", value: "Malayalam" },
  { label: "Kannada", value: "Kannada" },
  { label: "Bengali", value: "Bengali" },
  { label: "Marathi", value: "Marathi" },
  { label: "Gujarati", value: "Gujarati" },
  { label: "Punjabi", value: "Punjabi" },
  { label: "Odia", value: "Odia" },
  { label: "Assamese", value: "Assamese" },
  { label: "Nepali", value: "Nepali" },
  { label: "Bhojpuri", value: "Bhojpuri" },
];

export const PRODUCT_NAMES = [
  { label: "Accessory Gift Set", value: "Accessory Gift Set" },
  { label: "Action Figures and Toys", value: "Action Figures and Toys" },
  { label: "Activity Toys and Games", value: "Activity Toys and Games" },
  {
    label: "After Shave Lotion and Balm",
    value: "After Shave Lotion and Balm",
  },
  { label: "Air Freshener", value: "Air Freshener" },
  { label: "Air Fryer", value: "Air Fryer" },
  { label: "Air Purifiers", value: "Air Purifiers" },
  { label: "Anklet", value: "Anklet" },
  { label: "Appam Patra", value: "Appam Patra" },
  { label: "Appliance covers", value: "Appliance covers" },
  { label: "Aprons", value: "Aprons" },
  { label: "Aroma Oil Diffusers", value: "Aroma Oil Diffusers" },
  { label: "Aroma Oils", value: "Aroma Oils" },
  { label: "Art and Craft", value: "Art and Craft" },
  {
    label: "Artificial Flowers and Plants",
    value: "Artificial Flowers and Plants",
  },
  { label: "Ash Trays", value: "Ash Trays" },
  { label: "Assorted Gifts", value: "Assorted Gifts" },
  { label: "Attar", value: "Attar" },
  { label: "Baby Apparel Gift Set", value: "Baby Apparel Gift Set" },
  { label: "Baby Bath Accessories", value: "Baby Bath Accessories" },
  { label: "Baby Bath Sponges", value: "Baby Bath Sponges" },
  { label: "Baby Bathers", value: "Baby Bathers" },
  { label: "Baby Bed Sets", value: "Baby Bed Sets" },
  { label: "Baby Body Oil", value: "Baby Body Oil" },
  { label: "Baby Body Wash and Soap", value: "Baby Body Wash and Soap" },
  { label: "Baby Care Kit", value: "Baby Care Kit" },
  { label: "Baby Care Products", value: "Baby Care Products" },
  { label: "Baby Carriers", value: "Baby Carriers" },
  { label: "Baby Dolls", value: "Baby Dolls" },
  { label: "Baby Gear & Nursery", value: "Baby Gear & Nursery" },
  { label: "Baby Gear &amp; Nursery", value: "Baby Gear &amp; Nursery" },
  { label: "Baby Hair Brush", value: "Baby Hair Brush" },
  { label: "Baby Hair Oil", value: "Baby Hair Oil" },
  { label: "Baby Lotions and Creams", value: "Baby Lotions and Creams" },
  { label: "Baby Nail Grooming", value: "Baby Nail Grooming" },
  { label: "Baby Oral Care", value: "Baby Oral Care" },
  { label: "Baby Photoshoot Props", value: "Baby Photoshoot Props" },
  { label: "Baby Pillow", value: "Baby Pillow" },
  { label: "Baby Pool", value: "Baby Pool" },
  { label: "Baby Powder", value: "Baby Powder" },
  {
    label: "Baby Shampoo and Conditioner",
    value: "Baby Shampoo and Conditioner",
  },
  { label: "Baby Shower Caps", value: "Baby Shower Caps" },
  { label: "Baby Sippers", value: "Baby Sippers" },
  { label: "Baby Sleeping Bag", value: "Baby Sleeping Bag" },
  { label: "Baby Sunscreen", value: "Baby Sunscreen" },
  { label: "Baby Utensils", value: "Baby Utensils" },
  { label: "Baby Wipes and Buds", value: "Baby Wipes and Buds" },
  { label: "Backpacks", value: "Backpacks" },
  { label: "Badminton Racquets", value: "Badminton Racquets" },
  { label: "Badminton Shuttlecocks", value: "Badminton Shuttlecocks" },
  { label: "Bakeware", value: "Bakeware" },
  { label: "Baking Essentials", value: "Baking Essentials" },
  { label: "Bangle", value: "Bangle" },
  { label: "Bar and Drinkware", value: "Bar and Drinkware" },
  { label: "Bar Cabinet", value: "Bar Cabinet" },
  { label: "Bar Glass", value: "Bar Glass" },
  { label: "Bars", value: "Bars" },
  { label: "Barware Tool Sets", value: "Barware Tool Sets" },
  { label: "Basketballs", value: "Basketballs" },
  { label: "Bath and Body Combo", value: "Bath and Body Combo" },
  { label: "Bath and Body Gift Set", value: "Bath and Body Gift Set" },
  { label: "Bath Robe", value: "Bath Robe" },
  { label: "Bath Rugs", value: "Bath Rugs" },
  { label: "Bath Salt and Bubble Bath", value: "Bath Salt and Bubble Bath" },
  { label: "Bath Sets", value: "Bath Sets" },
  { label: "Bath Towels", value: "Bath Towels" },
  { label: "Bath Tub", value: "Bath Tub" },
  { label: "Bathroom Accessories", value: "Bathroom Accessories" },
  { label: "Bathroom Lights", value: "Bathroom Lights" },
  { label: "BB and CC Cream", value: "BB and CC Cream" },
  { label: "Beach Towels", value: "Beach Towels" },
  { label: "Bean Bag Cover", value: "Bean Bag Cover" },
  { label: "Beard Butter and Cream", value: "Beard Butter and Cream" },
  { label: "Beard Colour", value: "Beard Colour" },
  { label: "Beard Comb", value: "Beard Comb" },
  { label: "Beard Kit", value: "Beard Kit" },
  { label: "Beard Serum and Oil", value: "Beard Serum and Oil" },
  { label: "Beard Softener and Balm", value: "Beard Softener and Balm" },
  { label: "Beard Wash", value: "Beard Wash" },
  { label: "Beard Wax and Gel", value: "Beard Wax and Gel" },
  { label: "Beauty Combo", value: "Beauty Combo" },
  { label: "Beauty Gift Set", value: "Beauty Gift Set" },
  { label: "Bed Covers", value: "Bed Covers" },
  { label: "Bedding Set", value: "Bedding Set" },
  { label: "Bedsheets", value: "Bedsheets" },
  { label: "Bedside Table", value: "Bedside Table" },
  { label: "Beer Caddy", value: "Beer Caddy" },
  { label: "Beer Pitcher", value: "Beer Pitcher" },
  { label: "Belts", value: "Belts" },
  { label: "Bibs", value: "Bibs" },
  { label: "Bikini Trimmers", value: "Bikini Trimmers" },
  { label: "Bindi", value: "Bindi" },
  { label: "Bins", value: "Bins" },
  { label: "Blackhead Removal", value: "Blackhead Removal" },
  { label: "Blankets Quilts and Dohars", value: "Blankets Quilts and Dohars" },
  { label: "Blazers", value: "Blazers" },
  { label: "Bleach", value: "Bleach" },
  { label: "blouse", value: "blouse" },
  { label: "Blush", value: "Blush" },
  { label: "Blush Brush", value: "Blush Brush" },
  { label: "Body Butter", value: "Body Butter" },
  { label: "Body Cream and Lotion", value: "Body Cream and Lotion" },
  { label: "Body Groomer", value: "Body Groomer" },
  { label: "Body Mist and Spray", value: "Body Mist and Spray" },
  { label: "Body Scrub and Exfoliator", value: "Body Scrub and Exfoliator" },
  { label: "Body Sunscreen", value: "Body Sunscreen" },
  { label: "Body Wash and Shower Gel", value: "Body Wash and Shower Gel" },
  { label: "Body Wax and Essentials", value: "Body Wax and Essentials" },
  { label: "Bodysuit", value: "Bodysuit" },
  { label: "Bolster Covers", value: "Bolster Covers" },
  { label: "Bolsters", value: "Bolsters" },
  { label: "Book Shelf", value: "Book Shelf" },
  { label: "Booties", value: "Booties" },
  { label: "Boots", value: "Boots" },
  { label: "Bottle Cleaners", value: "Bottle Cleaners" },
  { label: "Bottle Holder", value: "Bottle Holder" },
  { label: "Bowl", value: "Bowl" },
  { label: "Box", value: "Box" },
  { label: "Boxers", value: "Boxers" },
  { label: "Boxes", value: "Boxes" },
  { label: "Bra", value: "Bra" },
  { label: "Bracelet", value: "Bracelet" },
  { label: "Bracelet Gold", value: "Bracelet Gold" },
  { label: "Bread Basket", value: "Bread Basket" },
  {
    label: "Breast Firming Gels and Creams",
    value: "Breast Firming Gels and Creams",
  },
  { label: "Breast Pumps", value: "Breast Pumps" },
  { label: "Briefs", value: "Briefs" },
  { label: "Bronzer", value: "Bronzer" },
  { label: "Brooch", value: "Brooch" },
  { label: "Brush Set", value: "Brush Set" },
  { label: "Burqas", value: "Burqas" },
  { label: "Cake", value: "Cake" },
  { label: "Cake Stand", value: "Cake Stand" },
  { label: "Cameras", value: "Cameras" },
  { label: "Camisoles", value: "Camisoles" },
  { label: "Candies", value: "Candies" },
  { label: "Candle Holders", value: "Candle Holders" },
  { label: "Candles", value: "Candles" },
  { label: "Capris", value: "Capris" },
  { label: "Caps", value: "Caps" },
  { label: "Car Air Purifier", value: "Car Air Purifier" },
  { label: "Car Seats", value: "Car Seats" },
  { label: "Car Vacuum Cleaner", value: "Car Vacuum Cleaner" },
  { label: "Carpets", value: "Carpets" },
  { label: "Carry Cot", value: "Carry Cot" },
  { label: "Casserole", value: "Casserole" },
  { label: "Casual Shoes", value: "Casual Shoes" },
  { label: "Cat Tshirts", value: "Cat Tshirts" },
  { label: "Ceiling Lamps", value: "Ceiling Lamps" },
  { label: "Cereal and Muesli", value: "Cereal and Muesli" },
  { label: "Chair Cover", value: "Chair Cover" },
  { label: "Chair Pads", value: "Chair Pads" },
  { label: "Charger", value: "Charger" },
  { label: "Charms", value: "Charms" },
  { label: "Chimney", value: "Chimney" },
  { label: "Chocolate Gifts", value: "Chocolate Gifts" },
  { label: "Chocolates", value: "Chocolates" },
  { label: "Chopper", value: "Chopper" },
  { label: "Chopping Board", value: "Chopping Board" },
  { label: "Churidar", value: "Churidar" },
  { label: "Cleaning Cloth", value: "Cleaning Cloth" },
  { label: "Cleansing Tools", value: "Cleansing Tools" },
  {
    label: "Cleansing Tools and Massagers",
    value: "Cleansing Tools and Massagers",
  },
  { label: "Clocks", value: "Clocks" },
  { label: "Clothing Fabric", value: "Clothing Fabric" },
  { label: "Clothing Set", value: "Clothing Set" },
  { label: "Clutches", value: "Clutches" },
  { label: "Co-Ords", value: "Co-Ords" },
  { label: "Coasters", value: "Coasters" },
  { label: "Coats", value: "Coats" },
  { label: "Cocktail Mix", value: "Cocktail Mix" },
  { label: "Cocktail Set", value: "Cocktail Set" },
  { label: "Cocktail Shakers", value: "Cocktail Shakers" },
  { label: "Cocktail Stirrers", value: "Cocktail Stirrers" },
  { label: "Coffee", value: "Coffee" },
  { label: "Coffee Bean Grinder", value: "Coffee Bean Grinder" },
  { label: "Coffee Gifts", value: "Coffee Gifts" },
  { label: "Coffee Press", value: "Coffee Press" },
  { label: "Coffee Table", value: "Coffee Table" },
  { label: "Cologne and After Shave", value: "Cologne and After Shave" },
  { label: "Compact", value: "Compact" },
  { label: "Concealer", value: "Concealer" },
  { label: "Conditioner", value: "Conditioner" },
  { label: "Condoms", value: "Condoms" },
  { label: "Construction Toys", value: "Construction Toys" },
  { label: "Container", value: "Container" },
  { label: "Contour", value: "Contour" },
  { label: "Cookies", value: "Cookies" },
  { label: "Cooking Handi", value: "Cooking Handi" },
  { label: "Cooking Oil", value: "Cooking Oil" },
  { label: "Cooking Pot", value: "Cooking Pot" },
  { label: "Cookware", value: "Cookware" },
  { label: "Cookware Set", value: "Cookware Set" },
  { label: "Corkscrews and Openers", value: "Corkscrews and Openers" },
  { label: "Corset", value: "Corset" },
  { label: "Cradles", value: "Cradles" },
  { label: "Cricket Balls", value: "Cricket Balls" },
  { label: "Cricket Bats", value: "Cricket Bats" },
  { label: "Cufflinks", value: "Cufflinks" },
  { label: "Cups and Mugs", value: "Cups and Mugs" },
  { label: "Curling Iron and Crimpers", value: "Curling Iron and Crimpers" },
  { label: "Curtain Rods and Brackets", value: "Curtain Rods and Brackets" },
  { label: "Curtain Tie and Hold Backs", value: "Curtain Tie and Hold Backs" },
  { label: "Curtains and Sheers", value: "Curtains and Sheers" },
  { label: "Cushion Covers", value: "Cushion Covers" },
  { label: "Cushions", value: "Cushions" },
  { label: "Cutlery Holder", value: "Cutlery Holder" },
  { label: "Cutlery Knife", value: "Cutlery Knife" },
  { label: "Cutlery Set", value: "Cutlery Set" },
  { label: "Cylinder trolley", value: "Cylinder trolley" },
  { label: "Day Cream", value: "Day Cream" },
  { label: "Decals and Stickers", value: "Decals and Stickers" },
  { label: "Decanter", value: "Decanter" },
  { label: "Decorative Bowls", value: "Decorative Bowls" },
  { label: "Decorative Magnets", value: "Decorative Magnets" },
  { label: "Deodorant", value: "Deodorant" },
  { label: "Dhotis", value: "Dhotis" },
  { label: "Diaper Bags", value: "Diaper Bags" },
  { label: "Diapers", value: "Diapers" },
  { label: "Dinner Set", value: "Dinner Set" },
  { label: "Dinnerware", value: "Dinnerware" },
  { label: "Dish rack", value: "Dish rack" },
  { label: "Diwan Set", value: "Diwan Set" },
  { label: "Dog Dresses", value: "Dog Dresses" },
  { label: "Dog Jackets", value: "Dog Jackets" },
  { label: "Dog Raincoats", value: "Dog Raincoats" },
  { label: "Dog Sweaters", value: "Dog Sweaters" },
  { label: "Dog Sweatshirts", value: "Dog Sweatshirts" },
  { label: "Dog Tshirts", value: "Dog Tshirts" },
  { label: "Door Lock", value: "Door Lock" },
  { label: "Doormats", value: "Doormats" },
  { label: "Drawers", value: "Drawers" },
  { label: "Dress Material", value: "Dress Material" },
  { label: "Dresses", value: "Dresses" },
  { label: "Dry Shampoo", value: "Dry Shampoo" },
  { label: "Dryers", value: "Dryers" },
  { label: "Duffel Bag", value: "Duffel Bag" },
  { label: "Dungarees", value: "Dungarees" },
  { label: "Dupatta", value: "Dupatta" },
  { label: "Duvet Cover", value: "Duvet Cover" },
  { label: "Earbuds Case", value: "Earbuds Case" },
  { label: "earring", value: "earring" },
  { label: "Earrings", value: "Earrings" },
  { label: "Earrings Diamond", value: "Earrings Diamond" },
  { label: "Earrings Gold", value: "Earrings Gold" },
  { label: "Egg Boiler", value: "Egg Boiler" },
  { label: "Egg Keeper", value: "Egg Keeper" },
  { label: "Electric Barbecue", value: "Electric Barbecue" },
  { label: "Electric Chopper", value: "Electric Chopper" },
  { label: "Electric Coffee Maker", value: "Electric Coffee Maker" },
  { label: "Electric Kettle", value: "Electric Kettle" },
  { label: "Electric Pan", value: "Electric Pan" },
  { label: "Electric Pressure Cookers", value: "Electric Pressure Cookers" },
  { label: "Electric Toothbrush", value: "Electric Toothbrush" },
  { label: "Epilator", value: "Epilator" },
  { label: "Ethnic Dresses", value: "Ethnic Dresses" },
  { label: "Eye Brush", value: "Eye Brush" },
  { label: "Eye Makeup Remover", value: "Eye Makeup Remover" },
  { label: "Eye Mask and Patches", value: "Eye Mask and Patches" },
  { label: "Eye Primer", value: "Eye Primer" },
  { label: "Eyebrow Enhancer", value: "Eyebrow Enhancer" },
  { label: "Eyelash Curler", value: "Eyelash Curler" },
  { label: "Eyeliner", value: "Eyeliner" },
  { label: "Eyeshadow", value: "Eyeshadow" },
  { label: "Face Brush", value: "Face Brush" },
  { label: "Face Cleansing Tools", value: "Face Cleansing Tools" },
  { label: "Face Derma Roller", value: "Face Derma Roller" },
  { label: "Face Epilator", value: "Face Epilator" },
  { label: "Face Mist", value: "Face Mist" },
  { label: "Face Primer", value: "Face Primer" },
  { label: "Face Scrub and Exfoliator", value: "Face Scrub and Exfoliator" },
  { label: "Face Shield", value: "Face Shield" },
  { label: "Face Sunscreen", value: "Face Sunscreen" },
  { label: "Face Towels", value: "Face Towels" },
  { label: "Face Wash and Cleanser", value: "Face Wash and Cleanser" },
  { label: "Facial Kit", value: "Facial Kit" },
  { label: "Facial Oil", value: "Facial Oil" },
  { label: "Facial Wipes and Tissues", value: "Facial Wipes and Tissues" },
  { label: "False Eyelashes", value: "False Eyelashes" },
  { label: "Feeding Bottles", value: "Feeding Bottles" },
  { label: "Feeding Essentials", value: "Feeding Essentials" },
  { label: "Festive Decor", value: "Festive Decor" },
  { label: "Filter Coffee Maker", value: "Filter Coffee Maker" },
  { label: "Fitness Bands", value: "Fitness Bands" },
  { label: "Flats", value: "Flats" },
  { label: "Flip Flops", value: "Flip Flops" },
  { label: "Floor Lamps", value: "Floor Lamps" },
  { label: "Floor Mats & Dhurries", value: "Floor Mats & Dhurries" },
  { label: "Floor Mats &amp; Dhurries", value: "Floor Mats &amp; Dhurries" },
  { label: "Floss and Tongue Cleaner", value: "Floss and Tongue Cleaner" },
  { label: "Food Feeders", value: "Food Feeders" },
  { label: "Food Platter", value: "Food Platter" },
  { label: "Food Processors", value: "Food Processors" },
  { label: "Food Steamer", value: "Food Steamer" },
  { label: "Foot Cream and Scrubs", value: "Foot Cream and Scrubs" },
  { label: "Footballs", value: "Footballs" },
  { label: "Fork", value: "Fork" },
  { label: "Formal Shoes", value: "Formal Shoes" },
  { label: "Foundation", value: "Foundation" },
  { label: "Fountains", value: "Fountains" },
  { label: "Fragrance Combo", value: "Fragrance Combo" },
  { label: "Fragrance Gift Set", value: "Fragrance Gift Set" },
  { label: "Frames", value: "Frames" },
  { label: "Free Gifts", value: "Free Gifts" },
  { label: "Fresh Flowers", value: "Fresh Flowers" },
  { label: "Fruit and Vegetable Basket", value: "Fruit and Vegetable Basket" },
  { label: "Garden Accessories", value: "Garden Accessories" },
  { label: "Gas Lighter", value: "Gas Lighter" },
  { label: "Gas Oven Tandoor", value: "Gas Oven Tandoor" },
  { label: "Gas Stove", value: "Gas Stove" },
  { label: "Gloves", value: "Gloves" },
  { label: "Gold Coin", value: "Gold Coin" },
  { label: "Graters", value: "Graters" },
  { label: "Groceries", value: "Groceries" },
  { label: "Grooming Combo", value: "Grooming Combo" },
  { label: "Grooming Gift Set", value: "Grooming Gift Set" },
  { label: "Hair Accessory", value: "Hair Accessory" },
  { label: "Hair Brush and Comb", value: "Hair Brush and Comb" },
  { label: "Hair Care Combo", value: "Hair Care Combo" },
  { label: "Hair Care Gift Set", value: "Hair Care Gift Set" },
  { label: "Hair Colour", value: "Hair Colour" },
  { label: "Hair Derma Roller", value: "Hair Derma Roller" },
  { label: "Hair Extension", value: "Hair Extension" },
  { label: "Hair Gels and Wax", value: "Hair Gels and Wax" },
  { label: "Hair Masks", value: "Hair Masks" },
  { label: "Hair Oil", value: "Hair Oil" },
  { label: "Hair Rollers and Curlers", value: "Hair Rollers and Curlers" },
  { label: "Hair Serum", value: "Hair Serum" },
  { label: "Hair Spray", value: "Hair Spray" },
  { label: "Hair Supplements", value: "Hair Supplements" },
  { label: "Hammock Swing", value: "Hammock Swing" },
  { label: "Hand and Foot Masks", value: "Hand and Foot Masks" },
  { label: "Hand Blender", value: "Hand Blender" },
  { label: "Hand Cream", value: "Hand Cream" },
  { label: "Hand Sanitizers", value: "Hand Sanitizers" },
  { label: "Hand Towels", value: "Hand Towels" },
  { label: "Hand Wash", value: "Hand Wash" },
  { label: "Handbags", value: "Handbags" },
  { label: "Handheld Air Fresheners", value: "Handheld Air Fresheners" },
  { label: "Handkerchief", value: "Handkerchief" },
  { label: "Hanger", value: "Hanger" },
  { label: "Hard Drive case", value: "Hard Drive case" },
  { label: "Harem Pants", value: "Harem Pants" },
  { label: "Hat", value: "Hat" },
  { label: "Head Jewellery", value: "Head Jewellery" },
  { label: "Headband", value: "Headband" },
  { label: "Headphones", value: "Headphones" },
  { label: "Heels", value: "Heels" },
  { label: "Helmets", value: "Helmets" },
  { label: "Herbal Supplements", value: "Herbal Supplements" },
  { label: "Herbs and Seasonings", value: "Herbs and Seasonings" },
  { label: "High Chairs", value: "High Chairs" },
  { label: "Highlighter", value: "Highlighter" },
  { label: "Hip Flask", value: "Hip Flask" },
  { label: "Home Fragrance Set", value: "Home Fragrance Set" },
  { label: "Home Fragrances", value: "Home Fragrances" },
  { label: "Home Gift Sets", value: "Home Gift Sets" },
  { label: "Home Temple", value: "Home Temple" },
  { label: "Hooks and Holders", value: "Hooks and Holders" },
  { label: "Hot Chocolate", value: "Hot Chocolate" },
  { label: "Ice Buckets", value: "Ice Buckets" },
  { label: "Ice Moulds", value: "Ice Moulds" },
  { label: "Idli Maker", value: "Idli Maker" },
  { label: "Immunity Boosters", value: "Immunity Boosters" },
  { label: "Induction Cooktop", value: "Induction Cooktop" },
  { label: "Infant and Pre-School Toys", value: "Infant and Pre-School Toys" },
  { label: "Innerwear Vests", value: "Innerwear Vests" },
  { label: "Insect Repellent", value: "Insect Repellent" },
  { label: "Intimate Hygiene", value: "Intimate Hygiene" },
  { label: "Intimate Wipes", value: "Intimate Wipes" },
  { label: "Irons", value: "Irons" },
  { label: "Jackets", value: "Jackets" },
  { label: "Jackets Smart", value: "Jackets Smart" },
  { label: "Jeans", value: "Jeans" },
  { label: "Jeggings", value: "Jeggings" },
  { label: "Jewellery Set", value: "Jewellery Set" },
  { label: "Jibbitz", value: "Jibbitz" },
  { label: "Jodhpuri Pants", value: "Jodhpuri Pants" },
  { label: "Juicer Mixer Grinder", value: "Juicer Mixer Grinder" },
  { label: "Jumpsuit", value: "Jumpsuit" },
  { label: "juti", value: "juti" },
  { label: "Kadhai and Wok", value: "Kadhai and Wok" },
  { label: "Kajal", value: "Kajal" },
  { label: "Kaleeras", value: "Kaleeras" },
  { label: "Kettle", value: "Kettle" },
  { label: "Key chain", value: "Key chain" },
  { label: "Key Holders", value: "Key Holders" },
  { label: "Kids Chair", value: "Kids Chair" },
  { label: "Kitchen Gloves", value: "Kitchen Gloves" },
  { label: "Kitchen Linen Sets", value: "Kitchen Linen Sets" },
  { label: "Kitchen Tools", value: "Kitchen Tools" },
  { label: "Kitchen Towels", value: "Kitchen Towels" },
  { label: "Kitchen Trolley", value: "Kitchen Trolley" },
  { label: "Kitchen Weighing Scale", value: "Kitchen Weighing Scale" },
  { label: "Knife", value: "Knife" },
  { label: "Knife Sharpener", value: "Knife Sharpener" },
  { label: "kurta", value: "kurta" },
  { label: "Kurta Sets", value: "Kurta Sets" },
  { label: "Kurtas", value: "Kurtas" },
  { label: "kurti", value: "kurti" },
  { label: "Kurtis", value: "Kurtis" },
  { label: "Ladle", value: "Ladle" },
  {
    label: "Lamps Lanterns and Lamp Shades",
    value: "Lamps Lanterns and Lamp Shades",
  },
  { label: "Laptop Bag", value: "Laptop Bag" },
  { label: "Laptop Table", value: "Laptop Table" },
  { label: "Laundry Bag", value: "Laundry Bag" },
  {
    label: "Learning and Development Toys",
    value: "Learning and Development Toys",
  },
  { label: "Leggings", value: "Leggings" },
  { label: "Lehenga Choli", value: "Lehenga Choli" },
  { label: "Lemon Squeezers", value: "Lemon Squeezers" },
  { label: "Lingerie accessories", value: "Lingerie accessories" },
  { label: "Lingerie Set", value: "Lingerie Set" },
  { label: "Lip Balm", value: "Lip Balm" },
  { label: "Lip Brush", value: "Lip Brush" },
  { label: "Lip Gloss", value: "Lip Gloss" },
  { label: "Lip Liner", value: "Lip Liner" },
  { label: "Lip Mask", value: "Lip Mask" },
  { label: "Lip Plumper", value: "Lip Plumper" },
  { label: "Lip Scrub", value: "Lip Scrub" },
  { label: "Lipstick", value: "Lipstick" },
  { label: "Live Plants", value: "Live Plants" },
  { label: "loafer", value: "loafer" },
  { label: "Loofah Sponges and Brushes", value: "Loofah Sponges and Brushes" },
  { label: "Loose Powder", value: "Loose Powder" },
  { label: "Lounge Chair", value: "Lounge Chair" },
  { label: "Lounge Pants", value: "Lounge Pants" },
  { label: "Lounge Shorts", value: "Lounge Shorts" },
  { label: "Lounge Tshirts", value: "Lounge Tshirts" },
  { label: "Lubricants", value: "Lubricants" },
  { label: "Luggage Rack", value: "Luggage Rack" },
  { label: "Lunch Box", value: "Lunch Box" },
  { label: "Lungi", value: "Lungi" },
  { label: "Magazine Holder", value: "Magazine Holder" },
  { label: "Makeup Combo", value: "Makeup Combo" },
  { label: "Makeup Gift Set", value: "Makeup Gift Set" },
  { label: "Makeup Kit", value: "Makeup Kit" },
  { label: "Makeup Pouch", value: "Makeup Pouch" },
  { label: "Makeup Remover", value: "Makeup Remover" },
  { label: "Mangalsutra", value: "Mangalsutra" },
  { label: "Manicure and Pedicure Kit", value: "Manicure and Pedicure Kit" },
  { label: "Mascara", value: "Mascara" },
  { label: "Masher", value: "Masher" },
  { label: "Mask and Peel", value: "Mask and Peel" },
  { label: "Mask Chains", value: "Mask Chains" },
  { label: "Massage Oils", value: "Massage Oils" },
  { label: "Mattress Protector", value: "Mattress Protector" },
  { label: "Measuring Cup", value: "Measuring Cup" },
  { label: "Messenger Bag", value: "Messenger Bag" },
  { label: "Mirrors", value: "Mirrors" },
  { label: "Mittens", value: "Mittens" },
  { label: "Mobile Accessories", value: "Mobile Accessories" },
  { label: "Mortar and Pestle", value: "Mortar and Pestle" },
  { label: "Mosquito Nets", value: "Mosquito Nets" },
  { label: "Mouthwash", value: "Mouthwash" },
  { label: "Muddler", value: "Muddler" },
  { label: "Mufflers", value: "Mufflers" },
  { label: "Multi-Styler", value: "Multi-Styler" },
  { label: "Multivitamins", value: "Multivitamins" },
  { label: "Munchies", value: "Munchies" },
  { label: "Musical Toys", value: "Musical Toys" },
  { label: "Nail Art and Nail Care", value: "Nail Art and Nail Care" },
  { label: "Nail Polish", value: "Nail Polish" },
  { label: "Nail Polish Remover", value: "Nail Polish Remover" },
  { label: "Necklace and Chains", value: "Necklace and Chains" },
  {
    label: "Necklace and Chains Diamond",
    value: "Necklace and Chains Diamond",
  },
  { label: "Necklace Gold", value: "Necklace Gold" },
  { label: "Nehru Jackets", value: "Nehru Jackets" },
  { label: "Nesting Table", value: "Nesting Table" },
  { label: "Night Cream", value: "Night Cream" },
  { label: "Night suits", value: "Night suits" },
  { label: "Nightdress", value: "Nightdress" },
  { label: "Nipple Creams and Butter", value: "Nipple Creams and Butter" },
  { label: "Nosepin", value: "Nosepin" },
  { label: "Nosepin Diamond", value: "Nosepin Diamond" },
  { label: "Nosepin Gold", value: "Nosepin Gold" },
  { label: "Nutrition and Wellbeing", value: "Nutrition and Wellbeing" },
  {
    label: "Nuts Seeds and Dried Fruits",
    value: "Nuts Seeds and Dried Fruits",
  },
  { label: "Oil Dispenser", value: "Oil Dispenser" },
  { label: "Organisers", value: "Organisers" },
  { label: "Other Kitchen Tools", value: "Other Kitchen Tools" },
  { label: "Ottomans", value: "Ottomans" },
  { label: "Outdoor Lamps", value: "Outdoor Lamps" },
  { label: "Outdoor Masks", value: "Outdoor Masks" },
  { label: "Oven Glove", value: "Oven Glove" },
  { label: "Oven Toaster Grill", value: "Oven Toaster Grill" },
  { label: "Padded Brief", value: "Padded Brief" },
  { label: "Pagri and Safa", value: "Pagri and Safa" },
  { label: "Palazzos", value: "Palazzos" },
  { label: "Pan", value: "Pan" },
  { label: "Pantyliners", value: "Pantyliners" },
  { label: "Pastas", value: "Pastas" },
  { label: "Patiala", value: "Patiala" },
  { label: "Patiala and Dupatta", value: "Patiala and Dupatta" },
  { label: "Peeler", value: "Peeler" },
  { label: "Peg Measure", value: "Peg Measure" },
  { label: "Pendant", value: "Pendant" },
  { label: "Pendant Diamond", value: "Pendant Diamond" },
  { label: "Pendant Gold", value: "Pendant Gold" },
  { label: "Pens", value: "Pens" },
  { label: "Perfume", value: "Perfume" },
  { label: "Personal Care Hamper", value: "Personal Care Hamper" },
  { label: "Pet Bandanas", value: "Pet Bandanas" },
  { label: "Pet Beds", value: "Pet Beds" },
  { label: "Pet Blankets", value: "Pet Blankets" },
  { label: "Pet Bowls", value: "Pet Bowls" },
  { label: "Pet Bows and Ties", value: "Pet Bows and Ties" },
  { label: "Pet Brushes and Combs", value: "Pet Brushes and Combs" },
  { label: "Pet Collars", value: "Pet Collars" },
  { label: "Pet Conditioners", value: "Pet Conditioners" },
  { label: "Pet Crates and Carriers", value: "Pet Crates and Carriers" },
  { label: "Pet Diapers", value: "Pet Diapers" },
  { label: "Pet Feeders", value: "Pet Feeders" },
  { label: "Pet Harnesses", value: "Pet Harnesses" },
  { label: "Pet Leashes", value: "Pet Leashes" },
  { label: "Pet Mats", value: "Pet Mats" },
  { label: "Pet Name Tags", value: "Pet Name Tags" },
  { label: "Pet Perfumes", value: "Pet Perfumes" },
  { label: "Pet Scratchers", value: "Pet Scratchers" },
  { label: "Pet Shampoos", value: "Pet Shampoos" },
  { label: "Pet Socks", value: "Pet Socks" },
  { label: "Pet Tick and Flea", value: "Pet Tick and Flea" },
  { label: "Pet Toys", value: "Pet Toys" },
  { label: "Pet Training Pads", value: "Pet Training Pads" },
  { label: "Pet Wipes", value: "Pet Wipes" },
  { label: "Photo Albums", value: "Photo Albums" },
  { label: "Photo Frames", value: "Photo Frames" },
  { label: "Physical Gift Card", value: "Physical Gift Card" },
  { label: "Pillow Covers", value: "Pillow Covers" },
  { label: "Pillow Protector", value: "Pillow Protector" },
  { label: "Pillows", value: "Pillows" },
  { label: "Planters", value: "Planters" },
  { label: "Plates", value: "Plates" },
  { label: "Pocket Mirror", value: "Pocket Mirror" },
  { label: "Pocket Squares", value: "Pocket Squares" },
  { label: "Poly Bag", value: "Poly Bag" },
  { label: "Pooja Essentials", value: "Pooja Essentials" },
  { label: "Potpourri", value: "Potpourri" },
  { label: "Powdered Mix", value: "Powdered Mix" },
  { label: "Pre and Post Shave", value: "Pre and Post Shave" },
  { label: "Pre or Post Workout", value: "Pre or Post Workout" },
  {
    label: "Pregnancy and Fertility Kits",
    value: "Pregnancy and Fertility Kits",
  },
  { label: "Pressure Cooker", value: "Pressure Cooker" },
  { label: "Protective Bodysuits", value: "Protective Bodysuits" },
  { label: "Proteins", value: "Proteins" },
  { label: "Pyjamas", value: "Pyjamas" },
  { label: "Racks and Wardrobes", value: "Racks and Wardrobes" },
  { label: "Rain Jacket", value: "Rain Jacket" },
  { label: "Rain Suit", value: "Rain Suit" },
  { label: "Rain Trousers", value: "Rain Trousers" },
  { label: "Rakhi", value: "Rakhi" },
  { label: "Rakhi Gift Set", value: "Rakhi Gift Set" },
  { label: "Rakhi Gold", value: "Rakhi Gold" },
  { label: "Rash Cream", value: "Rash Cream" },
  { label: "Razors and Cartridges", value: "Razors and Cartridges" },
  { label: "Ready to Cook", value: "Ready to Cook" },
  { label: "Rice", value: "Rice" },
  { label: "Ride On Vehicles", value: "Ride On Vehicles" },
  { label: "Ring", value: "Ring" },
  { label: "Ring Diamond", value: "Ring Diamond" },
  { label: "Ring Gold", value: "Ring Gold" },
  { label: "Robe", value: "Robe" },
  { label: "Rockers", value: "Rockers" },
  { label: "Role Play and Playsets", value: "Role Play and Playsets" },
  { label: "Roll-Ons", value: "Roll-Ons" },
  { label: "Rolling pin and board", value: "Rolling pin and board" },
  { label: "Rompers", value: "Rompers" },
  { label: "Roti Box", value: "Roti Box" },
  { label: "Rucksacks", value: "Rucksacks" },
  { label: "Runners", value: "Runners" },
  { label: "Salt and Pepper Shakers", value: "Salt and Pepper Shakers" },
  { label: "Salwar", value: "Salwar" },
  { label: "Salwar and Dupatta", value: "Salwar and Dupatta" },
  { label: "Sandals", value: "Sandals" },
  { label: "Sandwich Maker", value: "Sandwich Maker" },
  { label: "Sanitary Napkins", value: "Sanitary Napkins" },
  { label: "Sanitizer Sprayer", value: "Sanitizer Sprayer" },
  { label: "Saree Accessories", value: "Saree Accessories" },
  { label: "Saree Blouse", value: "Saree Blouse" },
  { label: "Sarees", value: "Sarees" },
  { label: "Sauces and Spreads", value: "Sauces and Spreads" },
  { label: "Scarves", value: "Scarves" },
  { label: "School Essentials", value: "School Essentials" },
  { label: "Security Camera", value: "Security Camera" },
  { label: "Serum and Gel", value: "Serum and Gel" },
  { label: "Serving Handi", value: "Serving Handi" },
  { label: "Serving Kadhai", value: "Serving Kadhai" },
  { label: "Serving Tray", value: "Serving Tray" },
  { label: "Setting Spray", value: "Setting Spray" },
  { label: "Sexual Health Supplements", value: "Sexual Health Supplements" },
  { label: "Shampoo", value: "Shampoo" },
  { label: "Shapewear", value: "Shapewear" },
  { label: "Shavers", value: "Shavers" },
  { label: "Shaving and Hair Removal", value: "Shaving and Hair Removal" },
  { label: "Shaving Brushes", value: "Shaving Brushes" },
  { label: "Shaving Cream and Foam", value: "Shaving Cream and Foam" },
  { label: "Shawl", value: "Shawl" },
  { label: "Sheet Masks", value: "Sheet Masks" },
  { label: "Sherwani", value: "Sherwani" },
  { label: "Shirts", value: "Shirts" },
  { label: "Shoe Accessories", value: "Shoe Accessories" },
  { label: "Shoe Care", value: "Shoe Care" },
  { label: "Shoe Rack", value: "Shoe Rack" },
  { label: "Shorts", value: "Shorts" },
  { label: "Shower Caps and Headbands", value: "Shower Caps and Headbands" },
  { label: "Shower Curtains", value: "Shower Curtains" },
  { label: "Showpieces", value: "Showpieces" },
  { label: "Shrug", value: "Shrug" },
  { label: "Silver Coins", value: "Silver Coins" },
  { label: "Sindoor", value: "Sindoor" },
  { label: "Skin Care Combo", value: "Skin Care Combo" },
  { label: "Skin Care Gift Set", value: "Skin Care Gift Set" },
  { label: "Skin Supplements", value: "Skin Supplements" },
  { label: "Skirts", value: "Skirts" },
  { label: "Sleeping Bag", value: "Sleeping Bag" },
  { label: "Sleeping Mat", value: "Sleeping Mat" },
  { label: "Sleepsuit", value: "Sleepsuit" },
  { label: "Slips", value: "Slips" },
  { label: "Smart Jewellery", value: "Smart Jewellery" },
  { label: "Smart Light", value: "Smart Light" },
  { label: "Smart Watches", value: "Smart Watches" },
  { label: "Soap", value: "Soap" },
  { label: "Socks", value: "Socks" },
  { label: "Sofa Covers", value: "Sofa Covers" },
  { label: "Soft Toys and Dolls", value: "Soft Toys and Dolls" },
  { label: "Soup Maker", value: "Soup Maker" },
  { label: "Spatula", value: "Spatula" },
  { label: "Speakers", value: "Speakers" },
  { label: "Spice Box", value: "Spice Box" },
  { label: "Sponge and Applicator", value: "Sponge and Applicator" },
  { label: "Spoon", value: "Spoon" },
  { label: "Sports Accessories", value: "Sports Accessories" },
  { label: "Sports Equipment", value: "Sports Equipment" },
  { label: "Sports Sandals", value: "Sports Sandals" },
  { label: "Sports Shoes", value: "Sports Shoes" },
  { label: "Stand Mixer", value: "Stand Mixer" },
  { label: "Stationery", value: "Stationery" },
  { label: "Step Ladder", value: "Step Ladder" },
  { label: "Sterilizers", value: "Sterilizers" },
  { label: "Stockings", value: "Stockings" },
  { label: "Stoles", value: "Stoles" },
  { label: "Stool", value: "Stool" },
  { label: "Storage Drum", value: "Storage Drum" },
  { label: "Straighteners", value: "Straighteners" },
  { label: "Strainer", value: "Strainer" },
  {
    label: "Stretch Marks Cream and Oil",
    value: "Stretch Marks Cream and Oil",
  },
  { label: "String Lights", value: "String Lights" },
  { label: "Strollers", value: "Strollers" },
  { label: "Study Table", value: "Study Table" },
  { label: "Subscription", value: "Subscription" },
  { label: "Suits", value: "Suits" },
  { label: "Sunglasses", value: "Sunglasses" },
  { label: "Sunglasses Case", value: "Sunglasses Case" },
  { label: "Suspenders", value: "Suspenders" },
  { label: "Sweaters", value: "Sweaters" },
  { label: "Sweatshirts", value: "Sweatshirts" },
  { label: "Sweets", value: "Sweets" },
  { label: "Sweets Gifts", value: "Sweets Gifts" },
  { label: "Swim Bottoms", value: "Swim Bottoms" },
  { label: "Swim Tops", value: "Swim Tops" },
  { label: "Swimwear", value: "Swimwear" },
  { label: "Swimwear Accessories", value: "Swimwear Accessories" },
  { label: "Swimwear Cover up Bottom", value: "Swimwear Cover up Bottom" },
  { label: "Swimwear Cover up Set", value: "Swimwear Cover up Set" },
  { label: "Swimwear Cover up Top", value: "Swimwear Cover up Top" },
  { label: "Swings", value: "Swings" },
  { label: "Syrups", value: "Syrups" },
  { label: "Table Covers", value: "Table Covers" },
  { label: "Table Lamps", value: "Table Lamps" },
  { label: "Table Linen Sets", value: "Table Linen Sets" },
  { label: "Table Napkins", value: "Table Napkins" },
  { label: "Table Placemats", value: "Table Placemats" },
  { label: "Table Tennis Kits", value: "Table Tennis Kits" },
  { label: "Tablet Sleeve", value: "Tablet Sleeve" },
  { label: "Tablets", value: "Tablets" },
  { label: "Talcum Powder", value: "Talcum Powder" },
  { label: "Tampons and Menstrual Cups", value: "Tampons and Menstrual Cups" },
  { label: "Tapestry", value: "Tapestry" },
  { label: "Tasla", value: "Tasla" },
  { label: "Tawa", value: "Tawa" },
  { label: "Tea", value: "Tea" },
  { label: "Tea Gifts", value: "Tea Gifts" },
  { label: "Tea Set", value: "Tea Set" },
  { label: "Teeth and Dental Care", value: "Teeth and Dental Care" },
  { label: "Teether", value: "Teether" },
  { label: "Tennis Balls", value: "Tennis Balls" },
  { label: "Tent", value: "Tent" },
  { label: "Thermal Bottoms", value: "Thermal Bottoms" },
  { label: "Thermal Set", value: "Thermal Set" },
  { label: "Thermal Tops", value: "Thermal Tops" },
  { label: "Throws", value: "Throws" },
  { label: "Ties", value: "Ties" },
  { label: "Ties and Cufflinks", value: "Ties and Cufflinks" },
  { label: "Tights", value: "Tights" },
  { label: "Tissue Holders", value: "Tissue Holders" },
  { label: "Toasters", value: "Toasters" },
  { label: "Toe Rings", value: "Toe Rings" },
  { label: "Toner", value: "Toner" },
  { label: "Tongs", value: "Tongs" },
  { label: "Toothbrush", value: "Toothbrush" },
  {
    label: "Toothpaste and Tooth Powder",
    value: "Toothpaste and Tooth Powder",
  },
  { label: "Toothpick Holder", value: "Toothpick Holder" },
  { label: "top", value: "top" },
  { label: "Tope", value: "Tope" },
  { label: "Tops", value: "Tops" },
  { label: "Towel Set", value: "Towel Set" },
  { label: "Toy Vehicles", value: "Toy Vehicles" },
  { label: "Track Pants", value: "Track Pants" },
  { label: "Tracksuits", value: "Tracksuits" },
  { label: "Travel Accessory", value: "Travel Accessory" },
  { label: "Travel Kit", value: "Travel Kit" },
  { label: "Trimmer", value: "Trimmer" },
  { label: "Trivet", value: "Trivet" },
  { label: "Trolley Bag", value: "Trolley Bag" },
  { label: "Trousers", value: "Trousers" },
  { label: "Trunk", value: "Trunk" },
  { label: "Tshirts", value: "Tshirts" },
  { label: "Tunics", value: "Tunics" },
  { label: "Tweezers and Sharpener", value: "Tweezers and Sharpener" },
  { label: "Umbrellas", value: "Umbrellas" },
  { label: "Under Eye Concealer", value: "Under Eye Concealer" },
  {
    label: "Under Eye Creams and Serums",
    value: "Under Eye Creams and Serums",
  },
  { label: "Vacuum Cleaner", value: "Vacuum Cleaner" },
  { label: "Vase Fillers", value: "Vase Fillers" },
  { label: "Vases", value: "Vases" },
  { label: "Vegan Milk", value: "Vegan Milk" },
  { label: "Volleyballs", value: "Volleyballs" },
  { label: "Waffle Maker", value: "Waffle Maker" },
  { label: "Waist Pouch", value: "Waist Pouch" },
  { label: "Waistcoat", value: "Waistcoat" },
  { label: "Walkers", value: "Walkers" },
  { label: "Wall Art", value: "Wall Art" },
  { label: "Wall Decor", value: "Wall Decor" },
  { label: "Wall Lamps", value: "Wall Lamps" },
  { label: "Wall Shelves", value: "Wall Shelves" },
  { label: "Wallets", value: "Wallets" },
  { label: "Warmers", value: "Warmers" },
  { label: "Watch Gift Set", value: "Watch Gift Set" },
  { label: "Watch Organiser", value: "Watch Organiser" },
  { label: "Watch Straps", value: "Watch Straps" },
  { label: "Watches", value: "Watches" },
  { label: "Water Bottle", value: "Water Bottle" },
  { label: "Water Glass", value: "Water Glass" },
  { label: "Water Jugs", value: "Water Jugs" },
  { label: "Weight Gain Supplements", value: "Weight Gain Supplements" },
  { label: "Weight Loss Supplements", value: "Weight Loss Supplements" },
  { label: "Whisk", value: "Whisk" },
  { label: "Windchimes", value: "Windchimes" },
  { label: "Wine Accessory Sets", value: "Wine Accessory Sets" },
  { label: "Wine Racks", value: "Wine Racks" },
  { label: "Workout Accessories", value: "Workout Accessories" },
  { label: "Wristbands", value: "Wristbands" },
  { label: "Yoga Mats", value: "Yoga Mats" },
];
